import React from 'react';
import { Booking, Expense, Insight, ServiceType, ExpenseCategory, BusinessSettings } from '../types';
import { 
  Briefcase, 
  Coffee, 
  Compass, 
  MapPin, 
  Calendar, 
  TrendingUp, 
  TrendingDown, 
  Brain, 
  Plus, 
  Trash2, 
  Sparkles,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Info,
  AlertTriangle,
  Target,
  Sliders,
  ShieldAlert,
  Upload,
  Search,
  Filter,
  Download,
  FileText,
  Loader2,
  X,
  Globe,
  Check,
  Copy,
  ExternalLink,
  Building2,
  Flame,
  ArrowRight,
  ShieldCheck,
  Users,
  Banknote,
  Scale,
  Ban,
  RotateCcw
} from 'lucide-react';
import { InvoicePDFTemplate, generateInvoicePDF } from './InvoicePDF';
import { BusinessSettingsModal } from './BusinessSettingsModal';
import { getBusinessSettings } from '../utils/businessSettings';
import { getSubscriptionData } from '../utils/subscriptionManager';
import { calculateMargin, getMarginStatus, getStreamMargins, generateMarginInsights } from '../utils/margin';
import { getFocusTasks } from '../utils/focusService';
import { PrivacyAmount, PrivacyBlur } from './PrivacyAmount';
import { isDemoDataLoaded } from '../dbService';
import { useI18n } from '../utils/i18n';

interface DashboardProps {
  bookings: Booking[];
  expenses: Expense[];
  insights: Insight[];
  streams: ServiceType[];
  onAddBookingClick: () => void;
  onAddExpenseClick: () => void;
  onAddInsightClick: (prefilledType?: 'booking' | 'expense' | 'general', prefilledId?: string) => void;
  onDeleteBooking: (id: string) => void;
  onDeleteExpense: (id: string) => void;
  onNavigate: (path: string) => void;
  onQuickAddBooking?: (price: number, type: ServiceType, clientName: string) => void;
  onQuickAddExpense?: (amount: number, category: ExpenseCategory, linkedStream: ServiceType, note: string) => void;
  marginThreshold: number;
  onAddStream?: (name: string) => void;
  onRemoveStream?: (name: string) => void;
  isPrivacyMode?: boolean;
  onOpenInviteModal?: () => void;
  onLoadDemoData?: () => void;
  onClearDemoData?: () => void;
  onOpenReconciler?: () => void;
}

export function ServiceIcon({ type, className = "w-4 h-4" }: { type: ServiceType; className?: string }) {
  const normalized = type?.toLowerCase();
  if (normalized?.includes('salary') || normalized?.includes('payroll') || normalized?.includes('draw') || normalized?.includes('wage')) {
    return <Banknote className={`${className} text-emerald-400`} />;
  }
  if (normalized?.includes('team') || normalized?.includes('staff')) {
    return <Users className={`${className} text-emerald-400`} />;
  }
  if (normalized?.includes('consult')) {
    return <Briefcase className={`${className} text-teal-accent`} />;
  }
  if (normalized?.includes('retreat')) {
    return <Compass className={`${className} text-purple-400`} />;
  }
  if (normalized?.includes('coffee')) {
    return <Coffee className={`${className} text-amber-400`} />;
  }
  if (normalized?.includes('content') || normalized?.includes('media')) {
    return <Sparkles className={`${className} text-pink-400`} />;
  }
  if (normalized?.includes('coach') || normalized?.includes('train')) {
    return <Target className={`${className} text-blue-400`} />;
  }
  // Default fallback beautiful icon
  return <TrendingUp className={`${className} text-[#14B8A6]`} />;
}

export function ExpenseCategoryIcon({ cat, className = "w-4 h-4" }: { cat: ExpenseCategory; className?: string }) {
  switch (cat) {
    case 'salary':
      return <Users className={`${className} text-emerald-400`} />;
    case 'travel':
      return <MapPin className={`${className} text-blue-400`} />;
    case 'supplies':
      return <Briefcase className={`${className} text-teal-accent`} />;
    case 'rent':
      return <Compass className={`${className} text-rose-400`} />;
    case 'other':
      return <DollarSign className={`${className} text-amber-400`} />;
  }
}

export function DueDateBadge({ dueDateStr, isPaid }: { dueDateStr?: string; isPaid?: boolean }) {
  if (!dueDateStr) return null;
  const due = new Date(dueDateStr);
  if (isNaN(due.getTime())) return null;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());

  const diffMs = dueDay.getTime() - today.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  const formattedDate = due.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

  if (isPaid) {
    return (
      <span className="bg-[#1F2426] text-gray-400 text-[10px] font-medium px-2 py-0.5 rounded-md shrink-0">
        Due {formattedDate}
      </span>
    );
  }

  if (diffDays < 0) {
    const overdueDays = Math.abs(diffDays);
    return (
      <span
        className="bg-rose-500/10 text-rose-400 border border-rose-500/30 text-[10px] font-semibold px-2 py-0.5 rounded-md shrink-0 flex items-center gap-1 animate-pulse"
        title={`Overdue by ${overdueDays} day${overdueDays === 1 ? '' : 's'} (${formattedDate})`}
      >
        ⚠️ Overdue {overdueDays}d ({formattedDate})
      </span>
    );
  } else if (diffDays === 0) {
    return (
      <span
        className="bg-amber-500/15 text-amber-300 border border-amber-500/30 text-[10px] font-semibold px-2 py-0.5 rounded-md shrink-0 flex items-center gap-1"
        title={`Due today (${formattedDate})`}
      >
        ⚡ Due today
      </span>
    );
  } else {
    return (
      <span
        className="bg-sky-500/10 text-sky-300 border border-sky-500/20 text-[10px] font-semibold px-2 py-0.5 rounded-md shrink-0 flex items-center gap-1"
        title={`Due in ${diffDays} day${diffDays === 1 ? '' : 's'} (${formattedDate})`}
      >
        🕒 Due in {diffDays}d ({formattedDate})
      </span>
    );
  }
}

// Get dynamic greeting based on time of day
function getGreeting(): string {
  const hr = new Date().getHours();
  if (hr < 12) return `Good morning`;
  if (hr < 17) return `Good afternoon`;
  return `Good evening`;
}

export const Dashboard = React.memo(function Dashboard({
  bookings,
  expenses,
  insights,
  streams,
  onAddBookingClick,
  onAddExpenseClick,
  onAddInsightClick,
  onDeleteBooking,
  onDeleteExpense,
  onNavigate,
  onQuickAddBooking,
  onQuickAddExpense,
  marginThreshold,
  onAddStream,
  onRemoveStream,
  isPrivacyMode = false,
  onOpenInviteModal,
  onLoadDemoData,
  onClearDemoData,
  onOpenReconciler
}: DashboardProps) {
  
  const isDemoActive = isDemoDataLoaded({ bookings, expenses, insights, streams });
  const businessSettings = getBusinessSettings();
  const currencySymbol = businessSettings.currencySymbol || 'R';
  const { t } = useI18n();

  // Calculate Totals (excluding cancelled invoices)
  const activeBookings = bookings.filter(b => b.status !== 'cancelled');
  const totalIncome = activeBookings.reduce((sum, b) => sum + (Number(b.price) || 0), 0);
  const totalExpenses = expenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  const profit = totalIncome - totalExpenses;

  // Invoice Payment Status Totals
  const outstandingTotal = bookings.filter(b => (b.status || 'unpaid') === 'unpaid').reduce((sum, b) => sum + (Number(b.price) || 0), 0);
  const collectedTotal = bookings.filter(b => b.status === 'paid').reduce((sum, b) => sum + (Number(b.price) || 0), 0);

  // Quick Add Booking state
  const [quickBookingPrice, setQuickBookingPrice] = React.useState('');
  const [quickBookingClient, setQuickBookingClient] = React.useState('');
  const [quickBookingType, setQuickBookingType] = React.useState<ServiceType>(streams[0] || '');

  // Quick Add Expense state
  const [quickExpenseAmount, setQuickExpenseAmount] = React.useState('');
  const [quickExpenseNote, setQuickExpenseNote] = React.useState('');
  const [quickExpenseCategory, setQuickExpenseCategory] = React.useState<ExpenseCategory>('supplies');
  const [quickExpenseStream, setQuickExpenseStream] = React.useState<ServiceType>(streams[0] || '');

  // Custom Stream Management State
  const [isManagingStreams, setIsManagingStreams] = React.useState(() => {
    try {
      if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        return params.get('action') === 'manage-streams' || params.get('setup') === 'streams';
      }
    } catch {}
    return false;
  });
  const [newStreamInput, setNewStreamInput] = React.useState('');

  React.useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        if (params.get('action') === 'manage-streams' || params.get('setup') === 'streams') {
          setIsManagingStreams(true);
        }
      }
    } catch {}
  }, []);

  React.useEffect(() => {
    if (streams && streams.length > 0) {
      if (!quickBookingType) setQuickBookingType(streams[0]);
      if (!quickExpenseStream) setQuickExpenseStream(streams[0]);
    }
  }, [streams]);


  const handleQuickBookingSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const priceNum = parseFloat(quickBookingPrice);
    if (isNaN(priceNum) || priceNum <= 0) {
      return;
    }
    const client = quickBookingClient.trim() || 'Quick Client';
    if (onQuickAddBooking) {
      onQuickAddBooking(priceNum, quickBookingType, client);
      setQuickBookingPrice('');
      setQuickBookingClient('');
    }
  };

  const handleQuickExpenseSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const amountNum = parseFloat(quickExpenseAmount);
    if (isNaN(amountNum) || amountNum <= 0) {
      return;
    }
    const note = quickExpenseNote.trim() || 'Quick Expense';
    if (onQuickAddExpense) {
      onQuickAddExpense(amountNum, quickExpenseCategory, quickExpenseStream, note);
      setQuickExpenseAmount('');
      setQuickExpenseNote('');
    }
  };

  // Unified Chronological Activities (bookings + expenses combined)
  const unifiedActivities = [
    ...bookings.map(b => ({
      id: b.id,
      type: 'booking' as const,
      label: b.type,
      name: b.client_name,
      amount: b.price,
      date: b.date,
    })),
    ...expenses.map(e => ({
      id: e.id,
      type: 'expense' as const,
      label: e.category,
      name: e.note,
      amount: e.amount,
      date: e.date,
    }))
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 6);

  // Group by Calendar Month
  const getMonthLabel = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return 'Unknown Month';
      return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } catch (e) {
      return 'Unknown Month';
    }
  };

  const getMonthKey = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return 'unknown';
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      return `${year}-${month}`;
    } catch (e) {
      return 'unknown';
    }
  };

  const monthlyMap: Record<string, {
    monthLabel: string;
    income: number;
    expenses: number;
    bookingCount: number;
    expenseCount: number;
  }> = {};

  activeBookings.forEach(b => {
    const key = getMonthKey(b.date);
    const label = getMonthLabel(b.date);
    if (!monthlyMap[key]) {
      monthlyMap[key] = { monthLabel: label, income: 0, expenses: 0, bookingCount: 0, expenseCount: 0 };
    }
    monthlyMap[key].income += (Number(b.price) || 0);
    monthlyMap[key].bookingCount += 1;
  });

  expenses.forEach(e => {
    const key = getMonthKey(e.date);
    const label = getMonthLabel(e.date);
    if (!monthlyMap[key]) {
      monthlyMap[key] = { monthLabel: label, income: 0, expenses: 0, bookingCount: 0, expenseCount: 0 };
    }
    monthlyMap[key].expenses += e.amount;
    monthlyMap[key].expenseCount += 1;
  });

  const monthlyPerformance = Object.entries(monthlyMap)
    .map(([key, data]) => {
      const profit = data.income - data.expenses;
      const margin = data.income > 0 ? (profit / data.income) * 100 : 0;
      return {
        monthKey: key,
        ...data,
        profit,
        margin,
      };
    })
    .sort((a, b) => b.monthKey.localeCompare(a.monthKey));

  // Subscription Tier Quota Check (75% limit banner logic)
  const subData = getSubscriptionData();
  const planId = subData.currentPlanId || 'starter';
  const isTrialOrPaid = subData.status === 'trialing' || planId === 'pro' || planId === 'enterprise';

  const maxFreeItems = 10;
  const bookingsCount = bookings.length;
  const expensesCount = expenses.length;
  const maxItemCount = Math.max(bookingsCount, expensesCount);
  const usageRatio = maxItemCount / maxFreeItems;
  const usagePercentage = Math.min(100, Math.round(usageRatio * 100));

  // Banner displays when user reaches 75% or higher of free tier limits (8+ items out of 10)
  const isNearFreeTierLimit = !isTrialOrPaid && (usageRatio >= 0.75 || bookingsCount >= 8 || expensesCount >= 8);

  return (
    <div className="space-y-6 animate-fade-in">
      
      {/* 1. HEADER (SUBTLE BUT CLEAN) */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-[#1F2426] pb-4">
        <div>
          <h2 className="text-xl font-display font-semibold tracking-tight text-white flex items-center gap-1.5">
            {getGreeting()} ✨
          </h2>
          <p className="text-gray-400 text-xs sm:text-sm">
            AzuraOS gives a solo operator one place to manage multiple income streams, track live profit margins, and understand the financial reality of their business.
          </p>
        </div>
      </div>

      {/* ⚡ ONE NORTH-STAR OUTCOME BANNER */}
      <div className="p-3.5 bg-[#121617] border border-[#14B8A6]/30 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-gray-300 shadow-sm">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-[#14B8A6]/10 text-[#14B8A6] flex items-center justify-center font-bold text-xs shrink-0 border border-[#14B8A6]/25">
            ⚡
          </div>
          <div>
            <span className="text-white font-semibold">In 10 Minutes, Know What You Keep:</span>{' '}
            <span>Enter 1 stream and 1 expense (or drop a bank statement) to see your <strong>Net Safe Spendable Owner Cash</strong> after tax reserves.</span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {onOpenReconciler && (
            <button
              onClick={onOpenReconciler}
              className="px-2.5 py-1 bg-[#162020] hover:bg-[#1C2828] border border-[#14B8A6]/40 text-[#14B8A6] rounded-lg font-mono text-[11px] font-bold transition-all cursor-pointer flex items-center gap-1"
            >
              <span>⚡ AI Reconciler</span>
            </button>
          )}
          <button
            onClick={() => setIsManagingStreams(!isManagingStreams)}
            className="px-2.5 py-1 bg-[#14B8A6]/10 hover:bg-[#14B8A6]/20 text-[#14B8A6] rounded-lg font-mono text-[11px] font-bold transition-all cursor-pointer"
          >
            {isManagingStreams ? 'Close Streams' : '⚙️ Manage Streams'}
          </button>
        </div>
      </div>

      {/* 75% FREE TIER LIMIT REACHED - UPGRADE TO PRO BANNER */}
      {isNearFreeTierLimit && (
        <div className="bg-gradient-to-r from-amber-500/20 via-[#14B8A6]/20 to-teal-500/10 border-2 border-amber-500/40 rounded-2xl p-5 sm:p-6 space-y-4 shadow-xl relative overflow-hidden animate-fade-in">
          <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 rounded-full blur-2xl pointer-events-none" />
          
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 relative z-10">
            <div className="space-y-2 max-w-2xl">
              <div className="flex items-center gap-2.5 flex-wrap">
                <span className="px-2.5 py-1 bg-amber-500/20 text-amber-300 border border-amber-500/40 font-extrabold text-[10px] uppercase tracking-wider rounded-lg flex items-center gap-1.5 shadow-sm">
                  <AlertTriangle size={13} className="text-amber-400" />
                  <span>Capacity Warning: {usagePercentage}% Free-Tier Quota Used</span>
                </span>
                <span className="text-xs text-gray-300 font-medium">
                  ({maxItemCount} of {maxFreeItems} Free Items Logged)
                </span>
              </div>

              <h3 className="text-lg sm:text-xl font-display font-bold text-white tracking-tight flex items-center gap-2">
                <Sparkles size={20} className="text-[#14B8A6]" />
                <span>You’re Approaching Your Solopreneur Free Limit ({usagePercentage}%)</span>
              </h3>

              <p className="text-xs text-gray-200 leading-relaxed">
                You have logged <strong>{maxItemCount} / {maxFreeItems} free entries</strong> ({bookingsCount} bookings, {expensesCount} expenses). Upgrade to <strong>Sovereign Founder Pro</strong> to unlock <strong>Unlimited Invoices &amp; Expenses</strong>, whitelisted multi-seats, and complete automated tax reports.
              </p>

              {/* Progress Bar */}
              <div className="space-y-1 pt-1 max-w-md">
                <div className="flex justify-between text-[10px] font-mono font-bold">
                  <span className="text-amber-300">Free Tier Usage ({maxItemCount}/{maxFreeItems})</span>
                  <span className="text-amber-400">{usagePercentage}%</span>
                </div>
                <div className="w-full bg-[#0C0E0F] h-2 rounded-full overflow-hidden border border-[#2A2F31]">
                  <div 
                    className="bg-gradient-to-r from-amber-400 to-[#14B8A6] h-full rounded-full transition-all duration-500" 
                    style={{ width: `${Math.min(100, usagePercentage)}%` }} 
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 shrink-0 pt-2 lg:pt-0">
              <button
                type="button"
                onClick={() => onNavigate('/settings')}
                className="px-5 py-3 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold text-xs rounded-xl shadow-lg shadow-[#14B8A6]/20 transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95"
              >
                <Sparkles size={16} />
                <span>Upgrade to Sovereign Pro</span>
                <ArrowRight size={14} />
              </button>
            </div>
          </div>

          <div className="pt-3 border-t border-amber-500/20 text-[11px] text-gray-300 flex items-center gap-1.5 font-medium">
            <ShieldCheck size={14} className="text-[#14B8A6] shrink-0" />
            <span>Try it first. If it’s not for you, you have 14 days to request a full refund. After that, cancel anytime.</span>
          </div>
        </div>
      )}

      {isDemoActive ? (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 animate-fade-in shadow-sm">
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-xl bg-amber-500/20 text-amber-400 shrink-0 mt-0.5 border border-amber-500/30">
              <Sparkles size={18} />
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-bold text-white">Sample Demo Data Active</h4>
                <span className="px-2 py-0.5 rounded text-[10px] bg-amber-400/20 text-amber-300 font-mono font-semibold border border-amber-400/30">Sample Sandbox Mode</span>
              </div>
              <p className="text-xs text-amber-200/90 leading-relaxed max-w-2xl">
                You are currently exploring AzuraOS with sample transactions ({currencySymbol}1,395 revenue, 3 bookings, {currencySymbol}247 expenses). Clear sample entries to begin tracking your real business with zero-balance accuracy.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto shrink-0">
            {onClearDemoData && (
              <button
                onClick={onClearDemoData}
                className="w-full sm:w-auto px-4 py-2.5 bg-amber-400 hover:bg-amber-300 text-black font-extrabold rounded-xl text-xs transition-all shadow-md flex items-center justify-center gap-1.5 cursor-pointer active:scale-95"
              >
                <Trash2 size={13} /> Clear Sample Data & Start Fresh
              </button>
            )}
          </div>
        </div>
      ) : totalIncome === 0 && totalExpenses === 0 ? (
        <div className="bg-gradient-to-r from-[#14B8A6]/15 via-[#14B8A6]/10 to-transparent border border-[#14B8A6]/25 rounded-2xl p-5 sm:p-6 space-y-3.5 shadow-lg shadow-[#14B8A6]/5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="space-y-1">
              <h4 className="text-sm font-bold text-white flex items-center gap-2 justify-start">
                <span className="p-1 bg-[#14B8A6]/20 rounded text-xs leading-none">✨</span> Clean Workspace Ready
              </h4>
              <p className="text-xs text-gray-300 leading-relaxed max-w-2xl">
                Your workspace starts at zero ({currencySymbol}0.00). Log your real income or expenses below to calculate profit margins in real time.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              <button
                onClick={() => onNavigate('/settings')}
                className="px-3.5 py-2 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold rounded-xl text-xs transition-all cursor-pointer flex items-center gap-1.5 shadow-md active:scale-95"
              >
                <Building2 size={13} /> Setup Business HQ
              </button>
              {onLoadDemoData && (
                <button
                  onClick={onLoadDemoData}
                  className="px-3 py-2 bg-[#14B8A6]/10 hover:bg-[#14B8A6]/20 border border-[#14B8A6]/30 text-teal-300 rounded-xl font-semibold text-xs transition-all cursor-pointer flex items-center gap-1.5 active:scale-95"
                >
                  <Sparkles size={12} /> Load Sample Data
                </button>
              )}
            </div>
          </div>

          <div className="pt-2.5 border-t border-[#14B8A6]/15 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-[11px] text-teal-300/90 font-medium">
            <span className="flex items-center gap-1.5">
              <Building2 size={13} className="text-[#14B8A6] shrink-0" />
              <span>✨ <strong>Quick Tip:</strong> Head over to <strong>Business HQ</strong> to set up your company profile, default currency, tax rules & bank details.</span>
            </span>
            <button
              onClick={() => onNavigate('/settings')}
              className="inline-flex items-center gap-1 text-[#14B8A6] hover:text-white transition-colors font-bold group cursor-pointer shrink-0"
            >
              <span>Go to Business HQ</span>
              <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        </div>
      ) : null}


      {/* 2. METRIC CARDS (THIS IS THE MONEY SHOT) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        
        {/* Income Card */}
        <div 
          onClick={() => onNavigate('/invoices')}
          className="bg-[#151819] border border-[#1F2426] rounded-2xl p-6 shadow-sm hover:translate-y-[-2px] hover:border-[#14B8A6]/30 transition-all duration-200 flex flex-col justify-between min-h-[140px] relative overflow-hidden group cursor-pointer"
          title="Click to view Invoices"
        >
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-gray-500 uppercase tracking-wider font-medium">{t('dash.income')}</span>
              <span className="w-1.5 h-1.5 rounded-full bg-[#14B8A6]" />
            </div>
            <p className="text-3xl font-semibold tracking-tight text-white font-display">
              <PrivacyAmount amount={totalIncome} prefix="R" isPrivacyMode={isPrivacyMode} />
            </p>
          </div>
          <div className="mt-4 pt-2 border-t border-[#1F2426] text-[11px] font-medium flex items-center justify-between gap-1">
            <span className="text-[#14B8A6]">💰 {t('dash.collected')}: <PrivacyAmount amount={collectedTotal} prefix="R" isPrivacyMode={isPrivacyMode} /></span>
            <span className="text-amber-400/90">💸 {t('dash.unpaid')}: <PrivacyAmount amount={outstandingTotal} prefix="R" isPrivacyMode={isPrivacyMode} /></span>
          </div>
        </div>

        {/* Expenses Card */}
        <div className="bg-[#151819] border border-[#1F2426] rounded-2xl p-6 shadow-sm hover:translate-y-[-2px] transition-all duration-200 flex flex-col justify-between min-h-[140px] group">
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-gray-500 uppercase tracking-wider font-medium">{t('dash.expenses')}</span>
              {/* No flashy accent */}
            </div>
            <p className="text-3xl font-semibold tracking-tight text-white font-display">
              <PrivacyAmount amount={totalExpenses} prefix="R" isPrivacyMode={isPrivacyMode} />
            </p>
          </div>
          <div className="mt-4 text-[11px] text-gray-500 font-medium">
            {t('dash.across_records')} ({expenses.length})
          </div>
        </div>

        {/* Profit Card (STAR OF THE SHOW) */}
        <div className="bg-[#151819] border border-[#1F2426] rounded-2xl p-6 shadow-sm hover:translate-y-[-2px] transition-all duration-200 flex flex-col justify-between min-h-[140px] relative overflow-hidden group md:scale-105 border-gold-accent/20">
          {/* Subtle gold glow blur element in background */}
          <div className="absolute top-0 right-0 w-20 h-20 bg-[#C6A15B]/5 rounded-full blur-xl pointer-events-none" />
          
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-[#C6A15B] uppercase tracking-wider font-semibold">{t('dash.net_profit')}</span>
              <Sparkles size={14} className="text-[#C6A15B] animate-pulse" />
            </div>
            <p className="text-3xl font-semibold tracking-tight text-[#C6A15B] font-display drop-shadow-[0_0_6px_rgba(198,161,91,0.3)]">
              <PrivacyAmount amount={profit} prefix="R" isPrivacyMode={isPrivacyMode} />
            </p>
          </div>
          <div className="mt-4 text-[11px] text-[#C6A15B] font-medium flex items-center justify-between">
            <span>+12% from last week</span>
            <span className="text-[10px] text-gray-500 font-normal">Margin: {totalIncome > 0 ? `${Math.round((profit / totalIncome) * 100)}%` : '0%'}</span>
          </div>
        </div>

      </div>

      {/* 2.2 DAILY FOCUS & REVENUE ACTION PLAN WIDGET */}
      {(() => {
        const focusTasks = getFocusTasks().filter(t => t.status !== 'done');
        const topTasks = focusTasks.slice(0, 3);
        const totalPendingRevenue = focusTasks.reduce((sum, t) => sum + (t.revenueImpact || 0), 0);

        return (
          <div className="bg-[#151819] border border-[#14B8A6]/30 rounded-2xl p-5 shadow-sm space-y-3 relative overflow-hidden">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-[#1F2426] pb-3">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-[#14B8A6]/10 border border-[#14B8A6]/30 flex items-center justify-center text-[#14B8A6]">
                  <Target size={15} />
                </div>
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-white flex items-center gap-2">
                    Daily Focus & Revenue Action Plan
                    {totalPendingRevenue > 0 && (
                      <span className="text-[10px] text-[#14B8A6] font-mono font-bold bg-[#14B8A6]/10 px-2 py-0.5 rounded-full border border-[#14B8A6]/20">
                        +{currencySymbol}{totalPendingRevenue.toLocaleString()} Potential
                      </span>
                    )}
                  </h3>
                  <p className="text-[11px] text-gray-400">High-leverage founder tasks driving profit and growth</p>
                </div>
              </div>

              <button
                onClick={() => onNavigate('/focus')}
                className="text-xs text-[#14B8A6] hover:underline font-bold flex items-center gap-1 shrink-0 cursor-pointer"
              >
                <span>Open Focus Roadmap</span>
                <ArrowRight size={13} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 pt-1">
              {topTasks.length === 0 ? (
                <div className="col-span-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl p-4 text-center space-y-1.5">
                  <p className="text-xs text-gray-400 font-medium">No active focus tasks in your roadmap.</p>
                  <button
                    onClick={() => onNavigate('/focus')}
                    className="text-xs text-[#14B8A6] font-bold hover:underline cursor-pointer"
                  >
                    + Set Revenue Focus Tasks
                  </button>
                </div>
              ) : (
                topTasks.map((task) => (
                  <div
                    key={task.id}
                    onClick={() => onNavigate('/focus')}
                    className="bg-[#0C0E0F] border border-[#2A2F31] hover:border-[#14B8A6]/40 rounded-xl p-3 flex flex-col justify-between gap-2 transition cursor-pointer group"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span
                          className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                            task.category === 'revenue'
                              ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                              : task.category === 'sales'
                              ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'
                              : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                          }`}
                        >
                          {task.category}
                        </span>
                        {task.revenueImpact && (
                          <span className="text-[10px] font-mono font-bold text-[#14B8A6]">
                            +{currencySymbol}{task.revenueImpact.toLocaleString()}
                          </span>
                        )}
                      </div>
                      <p className="text-xs font-semibold text-white line-clamp-2 leading-snug group-hover:text-[#14B8A6] transition-colors">
                        {task.title}
                      </p>
                    </div>

                    <div className="text-[10px] text-gray-500 font-bold uppercase tracking-wider flex items-center gap-1 pt-1 border-t border-[#1F2426]">
                      <Flame size={11} className="text-amber-400" />
                      <span>{task.priority} Priority</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        );
      })()}

      {/* 🤝 THE SMART TRIGGER (IN-APP REFERRAL NUDGE) */}
      <div className="bg-gradient-to-r from-[#151819] via-[#0C0E0F] to-[#151819] border border-[#14B8A6]/30 rounded-2xl p-5 shadow-md flex flex-col sm:flex-row items-center justify-between gap-4 relative overflow-hidden">
        <div className="absolute -left-10 -bottom-10 w-28 h-28 bg-[#14B8A6]/10 rounded-full blur-2xl pointer-events-none" />
        <div className="flex items-center gap-3.5 z-10">
          <div className="w-10 h-10 rounded-xl bg-[#14B8A6]/10 border border-[#14B8A6]/30 flex items-center justify-center text-[#14B8A6] shrink-0">
            <Sparkles size={20} />
          </div>
          <div className="space-y-0.5 text-center sm:text-left">
            <h4 className="text-sm font-bold text-white tracking-tight flex items-center justify-center sm:justify-start gap-1.5">
              <span>You’re now seeing your real margin.</span>
              <span className="text-[10px] font-mono text-[#14B8A6] bg-[#14B8A6]/10 px-2 py-0.5 rounded-full border border-[#14B8A6]/20">Founder Circle</span>
            </h4>
            <p className="text-xs text-gray-400">
              Most founders never do. If someone comes to mind — send them access.
            </p>
          </div>
        </div>
        <button
          onClick={onOpenInviteModal}
          className="w-full sm:w-auto bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-bold rounded-xl h-10 px-5 flex items-center justify-center gap-1.5 shadow-md shadow-[#14B8A6]/20 active:scale-95 transition-all cursor-pointer text-xs shrink-0 z-10"
        >
          <span>→ Invite a Founder</span>
        </button>
      </div>

      {/* 2.5 CORE MECHANIC: MARGIN % ENGINE ANALYSIS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        
        {/* Left Bento: Operational Stream Margins */}
        <div className="bg-[#151819] border border-[#1F2426] rounded-2xl p-5 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-[#14B8A6]/10 rounded-lg text-[#14B8A6]">
                <Target size={16} />
              </div>
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-white">Stream Profitability</h3>
                <p className="text-[10px] text-gray-400">1 offer or multiple streams • Live margin tracking</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsManagingStreams(!isManagingStreams)}
                className="text-[10px] font-bold text-[#14B8A6] px-2 py-1 bg-[#14B8A6]/10 border border-[#14B8A6]/20 rounded-lg hover:bg-[#14B8A6]/20 transition-all cursor-pointer focus:outline-none"
                title="Manage custom income streams"
              >
                {isManagingStreams ? 'Done' : '⚙️ Manage'}
              </button>
              {!isManagingStreams && (
                <span className="text-[10px] text-gray-500 font-medium font-mono hidden sm:inline">Goal: ≥{marginThreshold}%</span>
              )}
            </div>
          </div>

          {isManagingStreams && (
            <div className="bg-[#0C0E0F] border border-[#2A2F31] rounded-xl p-3.5 space-y-3 animate-fade-in text-xs">
              <div className="flex items-center justify-between text-gray-400 font-bold uppercase text-[9px] tracking-wider">
                <span>Manage Income Streams ({streams.length})</span>
                <span className="text-[9px] text-gray-500 font-normal capitalize">Enter new name below</span>
              </div>
              
              {/* Form to Add Stream */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newStreamInput}
                  onChange={(e) => setNewStreamInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const val = newStreamInput.trim();
                      if (!val) return;
                      if (onAddStream) {
                        onAddStream(val);
                        setNewStreamInput('');
                      }
                    }
                  }}
                  placeholder="e.g., E-commerce or Design"
                  className="flex-1 bg-[#151819] border border-[#1F2426] rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-[#14B8A6] placeholder-gray-600 font-medium"
                />
                <button
                  type="button"
                  onClick={() => {
                    const val = newStreamInput.trim();
                    if (!val) return;
                    if (onAddStream) {
                      onAddStream(val);
                      setNewStreamInput('');
                    }
                  }}
                  className="px-3 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-bold rounded-lg transition-colors flex items-center justify-center cursor-pointer text-xs"
                >
                  Add
                </button>
              </div>

              {/* Streams with trash option */}
              <div className="space-y-1.5 max-h-[140px] overflow-y-auto pr-1">
                {streams.map((str) => (
                  <div key={str} className="flex items-center justify-between p-2 bg-[#151819] rounded-lg border border-[#1F2426]">
                    <div className="flex items-center gap-2 capitalize font-semibold text-gray-300">
                      <ServiceIcon type={str} className="w-3.5 h-3.5 text-[#14B8A6]" />
                      <span>{str}</span>
                    </div>
                    {streams.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => {
                          if (onRemoveStream) {
                            onRemoveStream(str);
                          }
                        }}
                        className="text-gray-500 hover:text-rose-400 p-1 rounded hover:bg-rose-500/10 transition-colors cursor-pointer"
                        title={`Delete ${str}`}
                      >
                        <Trash2 size={12} />
                      </button>
                    ) : (
                      <span className="text-[9px] text-gray-600 italic">Min 1 required</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-3.5">
            {getStreamMargins(bookings, expenses, streams).map((stream) => {
              const labelMap: Record<ServiceType, string> = {
                consult: 'Consultancy',
                retreat: 'Premium Retreats',
                coffee: 'Coffee & Lead Gen'
              };
              
              const label = labelMap[stream.type] || stream.type;
              
              const getStreamGradient = (type: string) => {
                const norm = type.toLowerCase();
                if (norm.includes('consult')) return 'from-[#14B8A6] to-[#0EA5A4]';
                if (norm.includes('retreat')) return 'from-purple-500 to-indigo-500';
                if (norm.includes('coffee')) return 'from-amber-500 to-orange-500';
                if (norm.includes('content') || norm.includes('media')) return 'from-pink-500 to-rose-500';
                if (norm.includes('coach') || norm.includes('train')) return 'from-blue-500 to-teal-500';
                
                let sum = 0;
                for (let i = 0; i < type.length; i++) sum += type.charCodeAt(i);
                const colors = [
                  'from-[#14B8A6] to-[#0EA5A4]',
                  'from-purple-500 to-indigo-500',
                  'from-amber-500 to-orange-500',
                  'from-pink-500 to-rose-500',
                  'from-blue-500 to-teal-500',
                  'from-teal-500 to-emerald-500',
                  'from-indigo-500 to-blue-600'
                ];
                return colors[sum % colors.length];
              };
              
              const gradient = getStreamGradient(stream.type);
              
              let statusIndicator = '🟢';
              if (stream.margin < marginThreshold) {
                statusIndicator = '🔴';
              } else if (stream.margin <= marginThreshold + 5) {
                statusIndicator = '🟡';
              }
              
              return (
                <div key={stream.type} className="space-y-1.5 capitalize">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold text-gray-200 flex items-center gap-1.5">
                      <span className="text-[10px]">{statusIndicator}</span>
                      {label}
                    </span>
                    <span className="font-mono font-bold text-gray-300">
                      {stream.margin.toFixed(0)}% margin
                    </span>
                  </div>
                  
                  <div className="h-2 w-full bg-[#0C0E0F] rounded-full overflow-hidden border border-[#1F2426]">
                    <div 
                      className={`h-full rounded-full bg-gradient-to-r ${gradient}`}
                      style={{ width: `${Math.max(0, Math.min(100, stream.margin))}%` }}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between text-[10px] text-gray-500 font-mono">
                    <span>In: R{stream.income}</span>
                    <span>Out: R{stream.expense}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Bento: Built-in Operational Insights Engine (NOT advice) */}
        <div className="bg-[#151819] border border-[#1F2426] rounded-2xl p-5 shadow-sm space-y-4 flex flex-col justify-between">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-amber-500/10 rounded-lg text-amber-400">
                  <ShieldAlert size={16} />
                </div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-white">Built-In Insights Engine</h3>
              </div>
              <span className="text-[10px] bg-[#1F2426] text-gray-400 px-2 py-0.5 rounded-full font-mono">Real-Time</span>
            </div>

            <div className="space-y-2.5 max-h-[220px] overflow-y-auto pr-1">
              {generateMarginInsights(bookings, expenses, marginThreshold, streams).map((item) => {

                const colorTheme = item.type === 'alert' 
                  ? { border: 'border-rose-500/10', bg: 'bg-rose-500/5', text: 'text-rose-400', icon: '🔴' }
                  : item.type === 'caution'
                  ? { border: 'border-amber-500/10', bg: 'bg-amber-500/5', text: 'text-amber-400', icon: '🟡' }
                  : { border: 'border-[#14B8A6]/10', bg: 'bg-[#14B8A6]/5', text: 'text-[#14B8A6]', icon: '🟢' };
                  
                return (
                  <div 
                    key={item.id} 
                    className={`p-3 border rounded-xl flex items-start gap-2.5 transition-colors ${colorTheme.border} ${colorTheme.bg}`}
                  >
                    <span className="text-xs mt-0.5 shrink-0">{colorTheme.icon}</span>
                    <div className="space-y-1">
                      <p className="text-xs text-gray-300 leading-relaxed font-medium">
                        {item.text}
                      </p>
                      {item.metric && (
                        <span className={`text-[10px] font-mono font-bold uppercase ${colorTheme.text}`}>
                          Metric: {item.metric}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          <p className="text-[10px] text-gray-500 italic mt-3 pt-2 border-t border-[#1F2426]/50">
            * Note: These are objective metric calculations based purely on your inputs to assist pricing discipline, not financial advice.
          </p>
        </div>

      </div>

      {/* Monthly Performance Summary */}
      <div className="bg-[#151819] border border-[#1F2426] rounded-2xl p-5 shadow-sm space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-[#14B8A6]/10 rounded-lg text-[#14B8A6]">
              <TrendingUp size={16} />
            </div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-white">Monthly Performance</h3>
          </div>
          <span className="text-[10px] text-gray-500 font-medium font-mono">grouped by calendar month</span>
        </div>

        {monthlyPerformance.length === 0 ? (
          <div className="py-8 text-center text-xs text-gray-500">
            No logged bookings or expenses found to compile monthly performance metrics.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {monthlyPerformance.map((perf) => {
              const isProfit = perf.profit >= 0;
              const isHighMargin = perf.margin >= marginThreshold;

              return (
                <div 
                  key={perf.monthKey} 
                  className="bg-[#0C0E0F] border border-[#2A2F31] rounded-xl p-4 space-y-3 hover:border-[#14B8A6]/30 transition-all duration-200"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-sm text-gray-200">{perf.monthLabel}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono font-bold ${
                      isHighMargin 
                        ? 'bg-[#14B8A6]/10 text-[#14B8A6]' 
                        : 'bg-rose-500/10 text-rose-400'
                    }`}>
                      {perf.margin.toFixed(0)}% margin
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-[11px] border-y border-[#1F2426] py-2">
                    <div>
                      <span className="text-gray-500 block text-[10px]">Total Income</span>
                      <span className="font-semibold text-gray-300 font-mono">
                        R{perf.income.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 block text-[10px]">Total Expenses</span>
                      <span className="font-semibold text-gray-400 font-mono">
                        R{perf.expenses.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-1 text-xs">
                    <span className="text-gray-400">Net Profit</span>
                    <span className={`font-mono font-bold ${isProfit ? 'text-[#14B8A6]' : 'text-rose-400'}`}>
                      {isProfit ? '+' : ''}R{perf.profit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>

                  <div className="text-[10px] text-gray-500 flex justify-between">
                    <span>{perf.bookingCount} booking{perf.bookingCount !== 1 ? 's' : ''}</span>
                    <span>{perf.expenseCount} expense{perf.expenseCount !== 1 ? 's' : ''}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 3. QUICK ACTIONS (MAKE THIS FEEL ELITE) */}
      <div className="space-y-2 pt-2">
        <h3 className="text-xs text-gray-400 font-medium">{t('dash.quick_actions')}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <button
            onClick={onAddBookingClick}
            className="bg-[#151819] border border-[#2A2F31] rounded-xl p-4 min-h-[56px] flex items-center gap-3 hover:border-[#14B8A6] transition hover:translate-y-[-2px] duration-200 text-left w-full cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#14B8A6]"
          >
            <div className="w-8 h-8 rounded-lg bg-[#14B8A6]/10 flex items-center justify-center text-[#14B8A6] shrink-0">
              <Calendar size={18} />
            </div>
            <div>
              <span className="font-semibold text-sm text-gray-200 block">{t('dash.add_booking')}</span>
              <span className="text-[10px] text-gray-500">{t('dash.add_booking_desc')}</span>
            </div>
          </button>
 
          <button
            onClick={onAddExpenseClick}
            className="bg-[#151819] border border-[#2A2F31] rounded-xl p-4 min-h-[56px] flex items-center gap-3 hover:border-[#14B8A6] transition hover:translate-y-[-2px] duration-200 text-left w-full cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#14B8A6]"
          >
            <div className="w-8 h-8 rounded-lg bg-[#14B8A6]/10 flex items-center justify-center text-[#14B8A6] shrink-0">
              <DollarSign size={18} />
            </div>
            <div>
              <span className="font-semibold text-sm text-gray-200 block">{t('dash.add_expense')}</span>
              <span className="text-[10px] text-gray-500">{t('dash.add_expense_desc')}</span>
            </div>
          </button>
 
          <button
            onClick={() => onAddInsightClick('general', '')}
            className="bg-[#151819] border border-[#2A2F31] rounded-xl p-4 min-h-[56px] flex items-center gap-3 hover:border-[#14B8A6] transition hover:translate-y-[-2px] duration-200 text-left w-full cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#14B8A6]"
          >
            <div className="w-8 h-8 rounded-lg bg-[#14B8A6]/10 flex items-center justify-center text-[#14B8A6] shrink-0">
              <Brain size={18} />
            </div>
            <div>
              <span className="font-semibold text-sm text-gray-200 block">{t('dash.add_insight')}</span>
              <span className="text-[10px] text-gray-500">{t('dash.add_insight_desc')}</span>
            </div>
          </button>

        </div>
      </div>

      {/* QUICK ADD (INLINE) */}
      <div className="bg-[#151819] border border-[#1F2426] rounded-2xl p-5 shadow-sm space-y-4 hover:translate-y-[-1px] transition-all duration-200">
        <div className="flex items-center gap-1.5 text-xs text-gray-400 font-medium">
          <Sparkles size={14} className="text-[#14B8A6]" />
          <span>{t('dash.quick_add_title')}</span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          
          {/* Quick Booking Form */}
          <form onSubmit={handleQuickBookingSubmit} className="space-y-2 min-w-0">
            <div className="flex items-center justify-between text-[11px] text-gray-500 font-medium px-1">
              <span>{t('dash.quick_revenue')}</span>
              <span className="text-[#14B8A6] font-semibold text-[10px]">Active Stream</span>
            </div>
            
            <div className="flex items-center gap-1.5 sm:gap-2 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl px-2.5 sm:px-3 h-12 focus-within:border-[#14B8A6] transition-all duration-200 min-w-0">
              <span className="text-xs sm:text-sm font-semibold text-[#14B8A6] font-mono select-none shrink-0">R</span>
              
              <input
                type="number"
                step="any"
                value={quickBookingPrice}
                onChange={(e) => setQuickBookingPrice(e.target.value)}
                placeholder="Price"
                className="bg-transparent text-xs sm:text-sm text-white focus:outline-none w-12 sm:w-16 font-mono placeholder-gray-600 min-w-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                required
              />
              
              <div className="h-4 w-[1px] bg-[#1F2426] shrink-0" />
              
              <input
                type="text"
                value={quickBookingClient}
                onChange={(e) => setQuickBookingClient(e.target.value)}
                placeholder="Client"
                className="bg-transparent text-xs text-gray-300 focus:outline-none flex-1 min-w-[36px] placeholder-gray-600 truncate"
              />
              
              <div className="h-4 w-[1px] bg-[#1F2426] shrink-0" />
              
              <select
                value={quickBookingType}
                onChange={(e) => setQuickBookingType(e.target.value as ServiceType)}
                className="bg-[#151819] border border-[#1F2426] text-[10px] text-gray-300 rounded-md px-1 py-1 focus:outline-none focus:border-[#14B8A6] cursor-pointer capitalize max-w-[85px] sm:max-w-[120px] truncate shrink min-w-0"
              >
                {streams.map((str) => (
                  <option key={str} value={str} className="bg-[#151819]">
                    {str}
                  </option>
                ))}
              </select>

              <button
                type="submit"
                className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-[#14B8A6] text-black flex items-center justify-center hover:scale-105 active:scale-95 transition-all cursor-pointer shrink-0 ml-0.5 focus:outline-none"
                title="Save Booking"
              >
                <Plus size={16} className="stroke-[3px]" />
              </button>
            </div>
          </form>

          {/* Quick Expense Form */}
          <form onSubmit={handleQuickExpenseSubmit} className="space-y-2 min-w-0">
            <div className="flex items-center justify-between text-[11px] text-gray-500 font-medium px-1">
              <span>{t('dash.quick_expense')}</span>
              <span className="text-rose-400 font-semibold text-[10px]">Business Debit</span>
            </div>
            
            <div className="flex items-center gap-1.5 sm:gap-2 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl px-2.5 sm:px-3 h-12 focus-within:border-rose-400/50 transition-all duration-200 min-w-0">
              <span className="text-xs sm:text-sm font-semibold text-rose-400/80 font-mono select-none shrink-0">R</span>
              
              <input
                type="number"
                step="any"
                value={quickExpenseAmount}
                onChange={(e) => setQuickExpenseAmount(e.target.value)}
                placeholder="Cost"
                className="bg-transparent text-xs sm:text-sm text-white focus:outline-none w-12 sm:w-16 font-mono placeholder-gray-600 min-w-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                required
              />
              
              <div className="h-4 w-[1px] bg-[#1F2426] shrink-0" />
              
              <input
                type="text"
                value={quickExpenseNote}
                onChange={(e) => setQuickExpenseNote(e.target.value)}
                placeholder="Note"
                className="bg-transparent text-xs text-gray-300 focus:outline-none flex-1 min-w-[36px] placeholder-gray-600 truncate"
              />
              
              <div className="h-4 w-[1px] bg-[#1F2426] shrink-0" />
              
              <select
                value={quickExpenseCategory}
                onChange={(e) => setQuickExpenseCategory(e.target.value as ExpenseCategory)}
                className="bg-[#151819] border border-[#1F2426] text-[10px] text-gray-300 rounded-md px-1 py-1 focus:outline-none focus:border-rose-400/50 cursor-pointer max-w-[85px] sm:max-w-[120px] truncate shrink min-w-0"
                title="Category"
              >
                <option value="supplies">Supplies</option>
                <option value="travel">Travel</option>
                <option value="rent">Rent</option>
                <option value="other">Other</option>
              </select>
              
              <button
                type="submit"
                className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-[#E11D48] text-white flex items-center justify-center hover:scale-105 active:scale-95 transition-all cursor-pointer shrink-0 ml-0.5 focus:outline-none"
                title="Save Expense"
              >
                <Plus size={16} className="stroke-[3px]" />
              </button>
            </div>
          </form>

        </div>
      </div>

      {/* 4. RECENT ACTIVITY */}
      <div className="space-y-2 pt-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm text-gray-400">{t('dash.recent_activity')}</h3>
          <div className="flex gap-3">
            <button 
              onClick={() => onNavigate('/bookings')}
              className="text-[10px] font-semibold text-gray-500 hover:text-white transition-colors cursor-pointer"
            >
              {t('nav.bookings')}
            </button>
            <span className="text-[10px] text-gray-600">•</span>
            <button 
              onClick={() => onNavigate('/expenses')}
              className="text-[10px] font-semibold text-gray-500 hover:text-white transition-colors cursor-pointer"
            >
              {t('nav.expenses')}
            </button>
          </div>
        </div>

        <div className="bg-[#151819] border border-[#1F2426] rounded-2xl px-6 py-2 shadow-sm">
          {unifiedActivities.length === 0 ? (
            <div className="py-8 text-center text-xs text-gray-500">
              {t('dash.no_activity')}
            </div>
          ) : (
            unifiedActivities.map((act) => (
              <div 
                key={`${act.type}-${act.id}`}
                className="flex justify-between items-center py-3.5 border-b border-[#1F2426]/50 last:border-0 hover:bg-[#1C2022]/40 hover:translate-y-[-1px] transition-all duration-200 px-3 -mx-3 rounded-xl group/item cursor-pointer"
                onClick={() => onNavigate(act.type === 'booking' ? '/bookings' : '/expenses')}
              >
                <div>
                  <span className="capitalize font-medium text-sm text-gray-200 group-hover/item:text-[#14B8A6] transition-colors">
                    {act.type === 'booking' ? `${act.label}` : `Expense (${act.label})`}
                  </span>
                  {act.name && (
                    <span className="text-xs text-gray-500 block font-normal mt-0.5">
                      {act.name} • {new Date(act.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </span>
                  )}
                </div>
                <div className="text-right">
                  <span className={`font-mono font-semibold text-sm ${act.type === 'booking' ? 'text-[#14B8A6]' : 'text-rose-400/80'}`}>
                    {act.type === 'booking' ? '+' : '-'} <PrivacyAmount amount={act.amount} prefix="R" isPrivacyMode={isPrivacyMode} />
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Mini Spotlight Banner for J-Lens */}
      {insights.length > 0 && (
        <div className="bg-[#151819] border border-[#2A2F31] p-6 rounded-2xl space-y-3 relative overflow-hidden">
          <div className="absolute right-0 top-0 w-24 h-24 bg-gold-accent/5 rounded-full blur-xl pointer-events-none" />
          <div className="flex items-center gap-2">
            <span className="p-1 bg-gold-accent/10 rounded-lg text-gold-accent text-xs">🧠</span>
            <h4 className="font-display font-semibold text-xs uppercase tracking-wider text-gold-accent">{t('dash.spotlight_insight')}</h4>
          </div>
          <div className="space-y-1">
            <h5 className="font-bold text-white text-sm">{insights[0].title}</h5>
            <p className="text-xs text-gray-300 italic leading-relaxed">"{insights[0].note}"</p>
          </div>
          <div className="text-[10px] text-gray-500 flex items-center justify-between pt-2 border-t border-[#1F2426]">
            <span>Captured {new Date(insights[0].created_at).toLocaleDateString()}</span>
            <button 
              onClick={() => onNavigate('/j-lens')}
              className="text-gold-accent font-semibold hover:underline"
            >
              {t('dash.access_vault')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

// Bookings View (/bookings)
interface BookingsListProps {
  bookings: Booking[];
  onAddBookingClick: () => void;
  onDeleteBooking: (id: string) => void;
  onAddInsightClick: (prefilledType?: 'booking' | 'expense' | 'general', prefilledId?: string) => void;
  onUpdateBookingStatus?: (id: string, status: 'paid' | 'unpaid' | 'cancelled') => void;
  onImportCSVClick?: () => void;
  onNavigate?: (path: string) => void;
  isPrivacyMode?: boolean;
  onOpenReconciler?: () => void;
}

export const BookingsList = React.memo(function BookingsList({ 
  bookings, 
  onAddBookingClick, 
  onDeleteBooking, 
  onAddInsightClick, 
  onUpdateBookingStatus, 
  onImportCSVClick, 
  onNavigate,
  isPrivacyMode = false,
  onOpenReconciler
}: BookingsListProps) {
  const { t } = useI18n();
  const businessSettings = getBusinessSettings();
  const currencySymbol = businessSettings.currencySymbol || 'R';
  const [search, setSearch] = React.useState('');
  const [selectedType, setSelectedType] = React.useState<string>('all');
  const [copiedLink, setCopiedLink] = React.useState(false);
  const [showQuoteGenerator, setShowQuoteGenerator] = React.useState(false);
  const [customQuoteTitle, setCustomQuoteTitle] = React.useState('Strategy Audit');
  const [customQuotePrice, setCustomQuotePrice] = React.useState(350);

  const uniqueTypes = Array.from(new Set(bookings.map(b => b.type)));

  const filteredBookings = bookings.filter(b => {
    const matchesSearch = b.client_name.toLowerCase().includes(search.toLowerCase()) || 
                          b.type.toLowerCase().includes(search.toLowerCase());
    const matchesType = selectedType === 'all' || b.type === selectedType;
    return matchesSearch && matchesType;
  });

  const handleCopyLink = (customTitle?: string, customPrice?: number) => {
    let bookingUrl = `${window.location.origin}/book`;
    if (customPrice || customTitle) {
      const params = new URLSearchParams();
      if (customTitle) params.append('title', customTitle);
      if (customPrice) params.append('price', customPrice.toString());
      bookingUrl += `?${params.toString()}`;
    }
    navigator.clipboard.writeText(bookingUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-display font-semibold text-white">{t('bookings.title')}</h2>
          <p className="text-xs text-gray-400 mt-1">{t('bookings.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2.5 flex-wrap">
          {onOpenReconciler && (
            <button
              onClick={onOpenReconciler}
              className="bg-gradient-to-r from-teal-500/20 to-cyan-500/20 border border-teal-500/40 text-teal-300 hover:text-white hover:border-teal-400 transition-all font-semibold rounded-xl h-12 px-4 flex items-center gap-2 text-xs shadow-md active:scale-[0.98] cursor-pointer shrink-0"
              title="Auto-reconcile bank statements and receipts with AI"
            >
              <Sparkles size={15} className="text-teal-400 animate-pulse" />
              <span>AI Reconciler</span>
            </button>
          )}
          {onNavigate && (
            <button
              onClick={() => onNavigate('/book')}
              className="bg-[#151819] border border-[#14B8A6]/40 text-[#14B8A6] hover:bg-[#14B8A6]/10 transition-all font-semibold rounded-xl h-12 px-4 flex items-center gap-1.5 text-xs shadow-md active:scale-[0.98] cursor-pointer shrink-0"
            >
              <Globe size={15} /> {t('bookings.public_page')}
            </button>
          )}
          {onImportCSVClick && (
            <button
              onClick={onImportCSVClick}
              className="border border-[#2A2F31] text-gray-300 hover:border-[#14B8A6] hover:text-white bg-[#151819] transition-all font-semibold rounded-xl h-12 px-4 flex items-center gap-1.5 text-xs shadow-md active:scale-[0.98] cursor-pointer shrink-0"
            >
              <Upload size={15} /> {t('bookings.import_csv')}
            </button>
          )}
          <button
            onClick={onAddBookingClick}
            className="bg-[#14B8A6] text-black hover:bg-[#0EA5A4] transition-all font-semibold rounded-xl h-12 px-5 flex items-center gap-1.5 text-xs shadow-md active:scale-[0.98] cursor-pointer shrink-0"
          >
            <Plus size={16} /> {t('bookings.add_button')}
          </button>
        </div>
      </div>

      {/* Public Client Booking Share Banner */}
      <div className="bg-[#151819] border border-[#1F2426] rounded-2xl p-4 sm:p-5 space-y-4 shadow-lg relative overflow-hidden">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-xl bg-[#14B8A6]/10 border border-[#14B8A6]/30 flex items-center justify-center text-[#14B8A6] shrink-0">
              <Globe size={22} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold text-white">{t('bookings.client_link_title')}</h3>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#14B8A6]/10 text-[#14B8A6] border border-[#14B8A6]/20">
                  {t('bookings.link_active')}
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-0.5">
                {t('bookings.client_link_desc')}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto shrink-0 flex-wrap">
            <button
              onClick={() => setShowQuoteGenerator(!showQuoteGenerator)}
              className="px-3 py-2 bg-[#14B8A6]/10 text-[#14B8A6] border border-[#14B8A6]/30 hover:bg-[#14B8A6]/20 rounded-xl text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5"
            >
              <span>{showQuoteGenerator ? t('bookings.hide_custom_link') : t('bookings.generate_custom_link')}</span>
            </button>
            <button
              onClick={() => handleCopyLink()}
              className="px-3.5 py-2 bg-[#0C0E0F] hover:bg-[#1F2426] border border-[#2A2F31] text-gray-200 rounded-xl text-xs font-semibold transition-all cursor-pointer flex items-center justify-center gap-1.5"
            >
              {copiedLink ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
              <span>{copiedLink ? t('bookings.copied_link') : t('bookings.copy_standard_link')}</span>
            </button>
            {onNavigate && (
              <button
                onClick={() => onNavigate('/book')}
                className="px-3.5 py-2 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-bold rounded-xl text-xs transition-all active:scale-95 cursor-pointer shadow-md flex items-center justify-center gap-1.5"
              >
                <span>{t('bookings.preview')}</span>
                <ExternalLink size={13} />
              </button>
            )}
          </div>
        </div>

        {/* Inline Custom Quote Link Generator */}
        {showQuoteGenerator && (
          <div className="pt-3 border-t border-[#1F2426] bg-[#0C0E0F] p-4 rounded-xl space-y-3">
            <div className="flex items-center justify-between text-xs font-bold text-white">
              <span>Generate Custom Client Quote Link</span>
              <span className="text-[10px] text-gray-400 font-normal">Sends pre-configured session title & rate</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] font-bold text-gray-400 uppercase">Service / Session Title</label>
                <input
                  type="text"
                  value={customQuoteTitle}
                  onChange={(e) => setCustomQuoteTitle(e.target.value)}
                  placeholder="e.g. VIP Design Audit"
                  className="w-full mt-1 px-3 py-2 bg-[#151819] border border-[#2A2F31] rounded-xl text-xs text-white focus:outline-none focus:border-[#14B8A6]"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold text-gray-400 uppercase">Agreed Custom Rate ({currencySymbol})</label>
                <input
                  type="number"
                  min="1"
                  value={customQuotePrice}
                  onChange={(e) => setCustomQuotePrice(parseFloat(e.target.value) || 0)}
                  placeholder="350"
                  className="w-full mt-1 px-3 py-2 bg-[#151819] border border-[#2A2F31] rounded-xl text-xs font-bold text-emerald-400 focus:outline-none focus:border-[#14B8A6]"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => handleCopyLink(customQuoteTitle, customQuotePrice)}
                  className="w-full py-2 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-bold rounded-xl text-xs transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-md"
                >
                  {copiedLink ? <Check size={14} /> : <Copy size={14} />}
                  <span>Copy Link for {currencySymbol}{customQuotePrice}</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {bookings.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-3 bg-[#151819] border border-[#1F2426] p-4 rounded-2xl shadow-sm">
          {/* Search Input */}
          <div className="flex-1 flex items-center gap-2 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl px-3 h-11 focus-within:border-[#14B8A6] transition-all duration-200">
            <Search className="w-4 h-4 text-gray-500 shrink-0" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search bookings by client name or category..."
              className="bg-transparent text-xs sm:text-sm text-white focus:outline-none w-full placeholder-gray-600"
            />
            {search && (
              <button 
                onClick={() => setSearch('')}
                className="p-1 hover:bg-white/10 rounded-full transition-colors text-gray-400 hover:text-white"
                title="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Filter Dropdown */}
          <div className="sm:w-64 flex items-center gap-2 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl px-3 h-11 focus-within:border-[#14B8A6] transition-all duration-200 relative">
            <Filter className="w-4 h-4 text-gray-500 shrink-0" />
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="bg-transparent text-xs sm:text-sm text-gray-300 w-full focus:outline-none cursor-pointer capitalize appearance-none"
              title="Filter by stream category"
            >
              <option value="all" className="bg-[#151819]">All Categories</option>
              {uniqueTypes.map((t) => (
                <option key={t} value={t} className="bg-[#151819] capitalize">
                  {t}
                </option>
              ))}
            </select>
            {selectedType !== 'all' && (
              <button 
                onClick={() => setSelectedType('all')}
                className="p-1 hover:bg-white/10 rounded-full transition-colors text-gray-400 hover:text-white"
                title="Clear filter"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      )}

      <div className="bg-[#151819] rounded-2xl border border-[#1F2426] shadow-sm overflow-hidden">
        {bookings.length === 0 ? (
          <div className="p-8 sm:p-12 text-center relative overflow-hidden bg-gradient-to-b from-[#151819] via-[#0C0E0F] to-[#151819] border border-[#2A2F31] rounded-2xl">
            {/* Ambient Background Glows */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-[#14B8A6]/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/5 rounded-full blur-2xl pointer-events-none" />

            <div className="relative z-10 max-w-lg mx-auto space-y-6">
              {/* Hero Icon Visual Composite */}
              <div className="relative inline-flex items-center justify-center">
                <div className="absolute -inset-2 bg-gradient-to-r from-[#14B8A6]/20 via-cyan-500/20 to-[#14B8A6]/20 rounded-2xl blur-md" />
                <div className="w-20 h-20 rounded-2xl bg-[#0C0E0F] border border-[#14B8A6]/30 flex items-center justify-center text-[#14B8A6] shadow-xl relative">
                  <Calendar size={36} className="text-[#14B8A6]" />
                  <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-xl bg-[#14B8A6] text-black flex items-center justify-center shadow-lg border-2 border-[#0C0E0F]">
                    <Sparkles size={16} />
                  </div>
                </div>
              </div>

              {/* Welcoming Text */}
              <div className="space-y-2">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#14B8A6]/10 border border-[#14B8A6]/20 text-[11px] font-bold text-[#14B8A6] uppercase tracking-wider">
                  <span>Income Stream Ledger</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                  Ready to log your first booking?
                </h3>
                <p className="text-xs sm:text-sm text-gray-400 leading-relaxed max-w-md mx-auto">
                  Track client bookings, sessions, and custom income streams. AzuraOS auto-calculates gross revenue, invoice statuses, and stream-by-stream profit margins.
                </p>
              </div>

              {/* Feature highlights pills */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 py-2 text-[11px] font-semibold text-gray-300">
                <div className="p-2.5 rounded-xl bg-[#151819] border border-[#1F2426] flex items-center justify-center gap-2">
                  <TrendingUp size={14} className="text-[#14B8A6]" />
                  <span>Margin Auto-Sync</span>
                </div>
                <div className="p-2.5 rounded-xl bg-[#151819] border border-[#1F2426] flex items-center justify-center gap-2">
                  <Globe size={14} className="text-cyan-400" />
                  <span>Public Booking Page</span>
                </div>
                <div className="p-2.5 rounded-xl bg-[#151819] border border-[#1F2426] flex items-center justify-center gap-2">
                  <FileText size={14} className="text-amber-400" />
                  <span>PDF Invoicing</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
                <button
                  onClick={onAddBookingClick}
                  className="w-full sm:w-auto px-6 py-3 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold text-xs rounded-xl shadow-lg shadow-[#14B8A6]/20 transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95 shrink-0"
                >
                  <Plus size={16} />
                  <span>Add Your First Booking</span>
                </button>

                {onImportCSVClick && (
                  <button
                    onClick={onImportCSVClick}
                    className="w-full sm:w-auto px-5 py-3 bg-[#151819] hover:bg-[#1C2022] border border-[#2A2F31] hover:border-[#14B8A6]/40 text-gray-200 font-bold text-xs rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer shrink-0"
                  >
                    <Upload size={15} className="text-gray-400" />
                    <span>Import CSV Data</span>
                  </button>
                )}

                <button
                  onClick={() => handleCopyLink()}
                  className="w-full sm:w-auto px-5 py-3 bg-[#0C0E0F] hover:bg-[#1F2426] border border-[#2A2F31] text-gray-300 font-bold text-xs rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer shrink-0"
                >
                  {copiedLink ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                  <span>{copiedLink ? 'Link Copied!' : 'Copy Client Link'}</span>
                </button>
              </div>
            </div>
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="p-12 text-center text-gray-400 space-y-4">
            <Calendar className="w-12 h-12 mx-auto text-gray-600 opacity-60" />
            <div>
              <p className="font-medium text-sm text-white">No matching records found</p>
              <p className="text-xs text-gray-500 mt-1">We couldn't find any bookings matching "{search}" or filter criteria.</p>
            </div>
            <button
              onClick={() => {
                setSearch('');
                setSelectedType('all');
              }}
              className="px-4 py-2 bg-[#1F2426] text-xs font-semibold text-[#14B8A6] hover:bg-[#14B8A6]/10 rounded-xl border border-[#2A2F31] transition-all cursor-pointer"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="divide-y divide-[#1F2426]">
            {filteredBookings.map((b) => (
              <div key={b.id} className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-white/[0.01] transition-colors group">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-10 h-10 bg-[#0C0E0F] rounded-xl flex items-center justify-center shrink-0">
                    <ServiceIcon type={b.type} className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-bold text-white text-sm sm:text-base leading-snug break-words">{b.client_name}</h4>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <span className="px-2 py-0.5 bg-teal-accent/10 text-teal-accent text-[10px] font-semibold rounded-md capitalize shrink-0">
                        {b.type}
                      </span>
                      <span className="text-[10px] text-gray-600">•</span>
                      <span className="text-[10px] text-gray-400 font-medium shrink-0">
                        {new Date(b.date).toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
                      </span>
                      {b.due_date && (
                        <>
                          <span className="text-[10px] text-gray-600">•</span>
                          <DueDateBadge dueDateStr={b.due_date} isPaid={b.status === 'paid'} />
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4 w-full sm:w-auto pt-3 sm:pt-0 border-t border-[#1F2426]/50 sm:border-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-semibold text-sm sm:text-base text-white">
                      <PrivacyAmount amount={b.price} prefix="R" isPrivacyMode={isPrivacyMode} />
                    </span>
                    {b.status === 'paid' ? (
                      <span className="bg-[#14B8A6] text-black font-bold text-[10px] px-2 py-0.5 rounded-md shrink-0">
                        Paid ✓
                      </span>
                    ) : b.status === 'cancelled' ? (
                      <span className="bg-red-500/20 text-red-400 border border-red-500/30 font-bold text-[10px] px-2 py-0.5 rounded-md shrink-0">
                        Cancelled
                      </span>
                    ) : (
                      onUpdateBookingStatus && (
                        <button
                          type="button"
                          onClick={() => onUpdateBookingStatus(b.id, 'paid')}
                          className="bg-[#2A2F31] text-gray-400 hover:bg-[#14B8A6] hover:text-black transition-all text-[10px] font-semibold px-2 py-0.5 rounded-md shrink-0 cursor-pointer"
                          title="Mark Invoice as Paid"
                        >
                          Mark Paid
                        </button>
                      )
                    )}
                  </div>
                  
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <button
                      onClick={() => onAddInsightClick('booking', b.id)}
                      title="Attach J-Lens Insight"
                      className="w-8 h-8 sm:w-8 sm:h-8 flex items-center justify-center rounded-lg text-gold-accent hover:bg-gold-accent/15 transition-all cursor-pointer focus:outline-none focus:ring-1 focus:ring-gold-accent shrink-0 border border-gold-accent/20 hover:border-gold-accent/40"
                    >
                      <Brain size={14} />
                    </button>
                    <button
                      onClick={() => onDeleteBooking(b.id)}
                      title="Delete record"
                      className="w-8 h-8 sm:w-8 sm:h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-all cursor-pointer focus:outline-none focus:ring-1 focus:ring-red-400 shrink-0 border border-transparent hover:border-red-500/20"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

// Expenses View (/expenses)
interface ExpensesListProps {
  expenses: Expense[];
  onAddExpenseClick: () => void;
  onDeleteExpense: (id: string) => void;
  onAddInsightClick: (prefilledType?: 'booking' | 'expense' | 'general', prefilledId?: string) => void;
  onImportCSVClick?: () => void;
  isPrivacyMode?: boolean;
  onOpenReconciler?: () => void;
}

export const ExpensesList = React.memo(function ExpensesList({ 
  expenses, 
  onAddExpenseClick, 
  onDeleteExpense, 
  onAddInsightClick, 
  onImportCSVClick,
  isPrivacyMode = false,
  onOpenReconciler
}: ExpensesListProps) {
  const { t } = useI18n();
  const [search, setSearch] = React.useState('');
  const [selectedCategory, setSelectedCategory] = React.useState<string>('all');
  const [selectedStream, setSelectedStream] = React.useState<string>('all');

  const uniqueStreams = Array.from(new Set(expenses.map(e => e.linked_stream).filter(Boolean)));

  const filteredExpenses = expenses.filter(e => {
    const matchesSearch = e.note.toLowerCase().includes(search.toLowerCase()) || 
                          e.category.toLowerCase().includes(search.toLowerCase()) ||
                          e.linked_stream.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || e.category === selectedCategory;
    const matchesStream = selectedStream === 'all' || e.linked_stream === selectedStream;
    return matchesSearch && matchesCategory && matchesStream;
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-display font-semibold text-white">{t('expenses.title')}</h2>
          <p className="text-xs text-gray-400 mt-1">{t('expenses.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2.5 flex-wrap">
          {onOpenReconciler && (
            <button
              onClick={onOpenReconciler}
              className="bg-gradient-to-r from-teal-500/20 to-cyan-500/20 border border-teal-500/40 text-teal-300 hover:text-white hover:border-teal-400 transition-all font-semibold rounded-xl h-12 px-4 flex items-center gap-2 text-xs shadow-md active:scale-[0.98] cursor-pointer shrink-0"
              title="Auto-reconcile bank statements and scan receipts with AI"
            >
              <Sparkles size={15} className="text-teal-400 animate-pulse" />
              <span>AI Reconciler</span>
            </button>
          )}
          {onImportCSVClick && (
            <button
              onClick={onImportCSVClick}
              className="border border-[#2A2F31] text-gray-300 hover:border-[#14B8A6] hover:text-white bg-[#151819] transition-all font-semibold rounded-xl h-12 px-4 flex items-center gap-1.5 text-xs shadow-md active:scale-[0.98] cursor-pointer shrink-0"
            >
              <Upload size={15} /> {t('expenses.import_csv')}
            </button>
          )}
          <button
            onClick={onAddExpenseClick}
            className="bg-[#14B8A6] text-black hover:bg-[#0EA5A4] transition-all font-semibold rounded-xl h-12 px-5 flex items-center gap-1.5 text-xs shadow-md active:scale-[0.98] cursor-pointer shrink-0"
          >
            <Plus size={16} /> {t('expenses.log_expense')}
          </button>
        </div>
      </div>

      {expenses.length > 0 && (
        <div className="flex flex-col md:flex-row gap-3 bg-[#151819] border border-[#1F2426] p-4 rounded-2xl shadow-sm">
          {/* Search Input */}
          <div className="flex-1 flex items-center gap-2 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl px-3 h-11 focus-within:border-[#14B8A6] transition-all duration-200">
            <Search className="w-4 h-4 text-gray-500 shrink-0" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search expenses by note or categories..."
              className="bg-transparent text-xs sm:text-sm text-white focus:outline-none w-full placeholder-gray-600"
            />
            {search && (
              <button 
                onClick={() => setSearch('')}
                className="p-1 hover:bg-white/10 rounded-full transition-colors text-gray-400 hover:text-white"
                title="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Category Filter */}
          <div className="md:w-48 flex items-center gap-2 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl px-3 h-11 focus-within:border-[#14B8A6] transition-all duration-200 relative">
            <Filter className="w-4 h-4 text-gray-500 shrink-0" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="bg-transparent text-xs sm:text-sm text-gray-300 w-full focus:outline-none cursor-pointer capitalize appearance-none"
              title="Filter by expense category"
            >
              <option value="all" className="bg-[#151819]">All Categories</option>
              <option value="salary" className="bg-[#151819]">Salary & Payroll</option>
              <option value="supplies" className="bg-[#151819]">Supplies</option>
              <option value="travel" className="bg-[#151819]">Travel</option>
              <option value="rent" className="bg-[#151819]">Rent</option>
              <option value="other" className="bg-[#151819]">Other</option>
            </select>
            {selectedCategory !== 'all' && (
              <button 
                onClick={() => setSelectedCategory('all')}
                className="p-1 hover:bg-white/10 rounded-full transition-colors text-gray-400 hover:text-white"
                title="Clear category filter"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Stream Filter */}
          <div className="md:w-48 flex items-center gap-2 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl px-3 h-11 focus-within:border-[#14B8A6] transition-all duration-200 relative">
            <Filter className="w-4 h-4 text-gray-500 shrink-0" />
            <select
              value={selectedStream}
              onChange={(e) => setSelectedStream(e.target.value)}
              className="bg-transparent text-xs sm:text-sm text-gray-300 w-full focus:outline-none cursor-pointer capitalize appearance-none"
              title="Filter by linked stream"
            >
              <option value="all" className="bg-[#151819]">All Linked Streams</option>
              {uniqueStreams.map((s) => (
                <option key={s} value={s} className="bg-[#151819] capitalize">
                  {s}
                </option>
              ))}
            </select>
            {selectedStream !== 'all' && (
              <button 
                onClick={() => setSelectedStream('all')}
                className="p-1 hover:bg-white/10 rounded-full transition-colors text-gray-400 hover:text-white"
                title="Clear stream filter"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      )}

      <div className="bg-[#151819] rounded-2xl border border-[#1F2426] shadow-sm overflow-hidden">
        {expenses.length === 0 ? (
          <div className="p-8 sm:p-12 text-center relative overflow-hidden bg-gradient-to-b from-[#151819] via-[#0C0E0F] to-[#151819] border border-[#2A2F31] rounded-2xl">
            {/* Ambient Background Glows */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-rose-500/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full blur-2xl pointer-events-none" />

            <div className="relative z-10 max-w-lg mx-auto space-y-6">
              {/* Hero Icon Visual Composite */}
              <div className="relative inline-flex items-center justify-center">
                <div className="absolute -inset-2 bg-gradient-to-r from-rose-500/20 via-amber-500/20 to-rose-500/20 rounded-2xl blur-md" />
                <div className="w-20 h-20 rounded-2xl bg-[#0C0E0F] border border-rose-500/30 flex items-center justify-center text-rose-400 shadow-xl relative">
                  <DollarSign size={36} className="text-rose-400" />
                  <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-xl bg-rose-500 text-white flex items-center justify-center shadow-lg border-2 border-[#0C0E0F]">
                    <TrendingDown size={16} />
                  </div>
                </div>
              </div>

              {/* Welcoming Text */}
              <div className="space-y-2">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-500/10 border border-rose-500/20 text-[11px] font-bold text-rose-400 uppercase tracking-wider">
                  <span>Operational Ledger</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                  No operational expenses logged yet
                </h3>
                <p className="text-xs sm:text-sm text-gray-400 leading-relaxed max-w-md mx-auto">
                  Keep your business profitable by recording operational costs, supplies, rent, and software. Linking expenses directly to income streams gives you absolute net margin clarity.
                </p>
              </div>

              {/* Feature highlights pills */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 py-2 text-[11px] font-semibold text-gray-300">
                <div className="p-2.5 rounded-xl bg-[#151819] border border-[#1F2426] flex items-center justify-center gap-2">
                  <Target size={14} className="text-rose-400" />
                  <span>Stream Attribution</span>
                </div>
                <div className="p-2.5 rounded-xl bg-[#151819] border border-[#1F2426] flex items-center justify-center gap-2">
                  <Filter size={14} className="text-amber-400" />
                  <span>Category Audits</span>
                </div>
                <div className="p-2.5 rounded-xl bg-[#151819] border border-[#1F2426] flex items-center justify-center gap-2">
                  <Brain size={14} className="text-teal-400" />
                  <span>Profit Guard Rails</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
                <button
                  onClick={onAddExpenseClick}
                  className="w-full sm:w-auto px-6 py-3 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold text-xs rounded-xl shadow-lg shadow-[#14B8A6]/20 transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95 shrink-0"
                >
                  <Plus size={16} />
                  <span>Log First Expense</span>
                </button>

                {onImportCSVClick && (
                  <button
                    onClick={onImportCSVClick}
                    className="w-full sm:w-auto px-5 py-3 bg-[#151819] hover:bg-[#1C2022] border border-[#2A2F31] hover:border-[#14B8A6]/40 text-gray-200 font-bold text-xs rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer shrink-0"
                  >
                    <Upload size={15} className="text-gray-400" />
                    <span>Import Expense CSV</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : filteredExpenses.length === 0 ? (
          <div className="p-12 text-center text-gray-400 space-y-4">
            <DollarSign className="w-12 h-12 mx-auto text-gray-600 opacity-60" />
            <div>
              <p className="font-medium text-sm text-white">No matching records found</p>
              <p className="text-xs text-gray-500 mt-1">We couldn't find any expenses matching "{search}" or filter criteria.</p>
            </div>
            <button
              onClick={() => {
                setSearch('');
                setSelectedCategory('all');
                setSelectedStream('all');
              }}
              className="px-4 py-2 bg-[#1F2426] text-xs font-semibold text-[#14B8A6] hover:bg-[#14B8A6]/10 rounded-xl border border-[#2A2F31] transition-all cursor-pointer"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="divide-y divide-[#1F2426]">
            {filteredExpenses.map((e) => (
              <div key={e.id} className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-white/[0.01] transition-colors group">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-10 h-10 bg-[#0C0E0F] rounded-xl flex items-center justify-center shrink-0">
                    <ExpenseCategoryIcon cat={e.category} className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-bold text-white text-sm sm:text-base leading-snug break-words">{e.note}</h4>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <span className="px-2 py-0.5 bg-gray-800 text-gray-300 text-[10px] font-semibold rounded-md capitalize shrink-0">
                        {e.category}
                      </span>
                      {e.is_recurring && (
                        <span className="px-2 py-0.5 bg-rose-500/10 text-rose-400 text-[10px] font-semibold rounded-md capitalize shrink-0 flex items-center gap-1">
                          🔁 {e.frequency} overhead
                        </span>
                      )}
                      <span className="text-[10px] text-gray-600">•</span>
                      <span className="text-[10px] text-gray-400 font-medium">
                        Linked: <span className="text-teal-accent capitalize font-semibold">{e.linked_stream}</span>
                      </span>
                      <span className="text-[10px] text-gray-600">•</span>
                      <span className="text-[10px] text-gray-400 shrink-0">
                        {new Date(e.date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between sm:justify-end gap-4 w-full sm:w-auto pt-3 sm:pt-0 border-t border-[#1F2426]/50 sm:border-0">
                  <span className="font-mono font-semibold text-sm sm:text-base text-white">
                    <PrivacyAmount amount={e.amount} prefix="R" isPrivacyMode={isPrivacyMode} />
                  </span>
                  
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <button
                      onClick={() => onAddInsightClick('expense', e.id)}
                      title="Attach J-Lens Insight"
                      className="w-8 h-8 sm:w-8 sm:h-8 flex items-center justify-center rounded-lg text-gold-accent hover:bg-gold-accent/15 transition-all cursor-pointer focus:outline-none focus:ring-1 focus:ring-gold-accent shrink-0 border border-gold-accent/20 hover:border-gold-accent/40"
                    >
                      <Brain size={14} />
                    </button>
                    <button
                      onClick={() => onDeleteExpense(e.id)}
                      title="Delete expense"
                      className="w-8 h-8 sm:w-8 sm:h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-all cursor-pointer focus:outline-none focus:ring-1 focus:ring-red-400 shrink-0 border border-transparent hover:border-red-500/20"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

// J-Lens Vault (/j-lens) (Hidden page styled minimally, like a physical journal)
interface JLensListProps {
  insights: Insight[];
  bookings: Booking[];
  expenses: Array<Expense>;
  onDeleteInsight: (id: string) => void;
  onAddInsightClick: () => void;
}

export const JLensList = React.memo(function JLensList({ insights, bookings, expenses, onDeleteInsight, onAddInsightClick }: JLensListProps) {

  const findLinkedText = (type: string, id: string) => {
    if (type === 'booking') {
      const b = bookings.find(item => item.id === id);
      return b ? `Booking: ${b.client_name}` : 'Linked Booking';
    } else if (type === 'expense') {
      const e = expenses.find(item => item.id === id);
      return e ? `Expense: ${e.note}` : 'Linked Expense';
    }
    return '';
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl mx-auto">
      
      {/* Journal Header */}
      <div className="border-b border-[#2A2F31] pb-6 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="p-1 bg-gold-accent/10 rounded text-gold-accent text-sm">🧠</span>
            <h2 className="text-xl font-display font-semibold tracking-tight text-white">J-Lens Journal</h2>
          </div>
          <button
            onClick={onAddInsightClick}
            className="border border-[#2A2F31] text-gray-300 hover:border-gold-accent hover:text-white transition-all text-xs rounded-xl h-12 px-5 bg-[#151819] active:scale-[0.98] cursor-pointer flex items-center justify-center gap-1.5 focus:outline-none focus:ring-1 focus:ring-gold-accent"
          >
            <Plus size={16} /> New Note
          </button>
        </div>
        <p className="text-xs text-gray-400 leading-relaxed max-w-xl">
          Captured business reflections, pricing rules, customer triggers, and strategic observations. Minimal, encrypted, absolute clarity.
        </p>
      </div>

      {insights.length === 0 ? (
        <div className="bg-[#151819] p-16 text-center rounded-xl border border-[#2A2F31] text-gray-400">
          <Brain className="w-10 h-10 mx-auto text-gray-600 mb-3" />
          <p className="font-semibold text-sm">Journal is empty</p>
          <p className="text-xs text-gray-500 mt-1">Capture insights by clicking "+ Insight" or linking notes to client bookings.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {insights.map((ins) => {
            const linkedText = findLinkedText(ins.linked_type, ins.linked_id);
            return (
              <div 
                key={ins.id} 
                className="bg-[#151819] border border-[#2A2F31] rounded-xl p-5 hover:border-gold-accent/30 transition-all duration-300 relative flex flex-col justify-between group"
              >
                <div>
                  <div className="flex items-center justify-between gap-4 mb-2">
                    <h4 className="font-display font-medium text-sm text-white">{ins.title}</h4>
                    <button
                      onClick={() => onDeleteInsight(ins.id)}
                      title="Delete from journal"
                      className="w-11 h-11 flex items-center justify-center rounded-xl text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-colors shrink-0 cursor-pointer focus:outline-none focus:ring-1 focus:ring-red-400"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  
                  <p className="text-xs text-gray-400 leading-relaxed italic pr-4">
                    "{ins.note}"
                  </p>
                </div>

                <div className="border-t border-[#1F2426] pt-3 mt-4 flex items-center justify-between text-[10px] text-gray-500">
                  <span>Captured {new Date(ins.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  
                  {ins.linked_type !== 'general' && linkedText && (
                    <span className="text-gold-accent bg-gold-accent/10 border border-gold-accent/25 px-2 py-0.5 rounded text-[9px] font-semibold tracking-wider uppercase max-w-[180px] truncate">
                      {ins.linked_type}: {linkedText.split(':')[1]?.trim() || linkedText}
                    </span>
                  )}
                  {ins.linked_type === 'general' && (
                    <span className="text-gray-400 bg-white/5 px-2 py-0.5 rounded text-[9px] font-semibold tracking-wider uppercase">
                      General
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

// Invoices View (/invoices)
interface InvoicesListProps {
  bookings: Booking[];
  onUpdateBookingStatus: (id: string, status: 'paid' | 'unpaid' | 'cancelled') => void;
  onNavigate: (path: string) => void;
  onAddBookingClick?: () => void;
  isPrivacyMode?: boolean;
  onTogglePrivacyMode?: () => void;
}

export const InvoicesList = React.memo(function InvoicesList({ 
  bookings, 
  onUpdateBookingStatus, 
  onNavigate, 
  onAddBookingClick,
  isPrivacyMode = false,
  onTogglePrivacyMode
}: InvoicesListProps) {
  const [selectedInvoice, setSelectedInvoice] = React.useState<Booking | null>(null);
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<'all' | 'unpaid' | 'paid' | 'cancelled'>('all');
  const [pdfBooking, setPdfBooking] = React.useState<Booking | null>(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = React.useState(false);

  // Business Sender Profile State
  const [isSettingsOpen, setIsSettingsOpen] = React.useState(false);
  const [businessSettings, setBusinessSettings] = React.useState<BusinessSettings>(getBusinessSettings());

  // PDF Generator Trigger
  const handleDownloadPDF = async (booking: Booking, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setPdfBooking(booking);
    setIsGeneratingPDF(true);

    setTimeout(async () => {
      await generateInvoicePDF(booking, 'invoice-pdf-hidden-template');
      setIsGeneratingPDF(false);
    }, 120);
  };

  // Filter Bookings
  const filteredBookings = React.useMemo(() => {
    return bookings.filter(b => {
      const q = search.trim().toLowerCase();
      const matchesSearch =
        !q ||
        (b.client_name || '').toLowerCase().includes(q) ||
        (b.invoice_number || `INV-${b.id}`).toLowerCase().includes(q) ||
        (b.type || '').toLowerCase().includes(q);

      const status = b.status || 'unpaid';
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'paid' && status === 'paid') ||
        (statusFilter === 'unpaid' && status === 'unpaid') ||
        (statusFilter === 'cancelled' && status === 'cancelled');

      return matchesSearch && matchesStatus;
    });
  }, [bookings, search, statusFilter]);

  // Group & Calculate Summary
  const unpaidBookings = bookings.filter(b => (b.status || 'unpaid') === 'unpaid');
  const paidBookings = bookings.filter(b => b.status === 'paid');
  const cancelledBookings = bookings.filter(b => b.status === 'cancelled');

  const filteredUnpaid = filteredBookings.filter(b => (b.status || 'unpaid') === 'unpaid');
  const filteredPaid = filteredBookings.filter(b => b.status === 'paid');
  const filteredCancelled = filteredBookings.filter(b => b.status === 'cancelled');

  const outstandingTotal = unpaidBookings.reduce((sum, b) => sum + b.price, 0);
  const collectedTotal = paidBookings.reduce((sum, b) => sum + b.price, 0);
  const activeRevenueTotal = outstandingTotal + collectedTotal;

  // CSV Export
  const handleExportCSV = () => {
    if (filteredBookings.length === 0) return;
    const headers = ['Invoice Number', 'Client Name', 'Service Stream', 'Booking Date', 'Due Date', 'Subtotal (ZAR)', 'VAT 15% (ZAR)', 'Total incl VAT (ZAR)', 'Status', 'Created At'];
    const csvRows = [
      headers.join(','),
      ...filteredBookings.map(b => {
        const invNum = b.invoice_number || `INV-${b.id}`;
        const client = b.client_name || '';
        const stream = b.type || '';
        const bookingDate = b.date ? new Date(b.date).toLocaleDateString() : '';
        const dueDate = b.due_date ? new Date(b.due_date).toLocaleDateString() : '';
        const subtotal = b.price;
        const vat = subtotal * 0.15;
        const total = subtotal + vat;
        const status = b.status === 'paid' ? 'Paid' : b.status === 'cancelled' ? 'Cancelled' : 'Unpaid';
        const createdAt = b.created_at ? new Date(b.created_at).toLocaleDateString() : '';

        return [
          `"${invNum.replace(/"/g, '""')}"`,
          `"${client.replace(/"/g, '""')}"`,
          `"${stream.replace(/"/g, '""')}"`,
          `"${bookingDate}"`,
          `"${dueDate}"`,
          subtotal.toFixed(2),
          vat.toFixed(2),
          total.toFixed(2),
          `"${status}"`,
          `"${createdAt}"`
        ].join(',');
      })
    ];

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoices-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* 1. HEADER (Tablet, Mobile & Desktop Adaptive Layout) */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-[#1F2426] pb-4">
        <div>
          <h2 className="text-2xl font-display font-semibold text-white flex items-center gap-2">
            🧾 Invoices
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            Track receivables, paid invoices, and manage cancelled records
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 max-w-full">
          <button
            type="button"
            onClick={() => onNavigate ? onNavigate('/ai-assistant') : undefined}
            className="bg-[#151819] hover:bg-[#1F2426] text-teal-300 hover:text-white border border-teal-500/30 font-semibold rounded-xl h-10 px-3 flex items-center gap-1.5 text-xs transition-all cursor-pointer active:scale-95 shadow-sm whitespace-nowrap"
            title="Draft protective client agreements and milestone proforma invoices with your AI Co-Founder"
          >
            <Scale size={15} className="text-teal-400 shrink-0" />
            <span className="hidden xl:inline">Client Agreements &amp; Proforma</span>
            <span className="xl:hidden">Agreements</span>
          </button>
          <button
            type="button"
            onClick={() => onNavigate ? onNavigate('/settings') : setIsSettingsOpen(true)}
            className="bg-[#151819] hover:bg-[#1F2426] text-gray-200 border border-[#2A2F31] font-semibold rounded-xl h-10 px-3 flex items-center gap-1.5 text-xs transition-all cursor-pointer active:scale-95 whitespace-nowrap"
            title="Configure business profile, currency, tax rules & payment links"
          >
            <Building2 size={15} className="text-[#14B8A6] shrink-0" />
            <span className="hidden xl:inline">Business &amp; Tax HQ</span>
            <span className="xl:hidden">Tax HQ</span>
          </button>
          <button
            type="button"
            onClick={handleExportCSV}
            disabled={filteredBookings.length === 0}
            className={`font-semibold rounded-xl h-10 px-3 flex items-center gap-1.5 text-xs transition-all border whitespace-nowrap ${
              filteredBookings.length > 0
                ? 'bg-[#151819] text-gray-200 border-[#2A2F31] hover:bg-[#1F2426] hover:text-white cursor-pointer active:scale-95'
                : 'bg-[#151819]/50 text-gray-600 border-[#1F2426] cursor-not-allowed'
            }`}
            title={filteredBookings.length > 0 ? `Export ${filteredBookings.length} invoice(s) to CSV` : 'No invoices to export'}
          >
            <Download size={15} className="shrink-0" />
            <span>CSV</span>
          </button>
          {onAddBookingClick && (
            <button
              onClick={onAddBookingClick}
              className="bg-[#14B8A6] text-black hover:bg-[#0EA5A4] transition-all font-bold rounded-xl h-10 px-3.5 flex items-center gap-1.5 text-xs shadow-md active:scale-[0.98] cursor-pointer whitespace-nowrap shrink-0"
            >
              <Plus size={16} className="shrink-0" /> <span>Add Booking</span>
            </button>
          )}
        </div>
      </div>

      {/* 2. SUMMARY STRIP */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Outstanding */}
        <div className="bg-[#151819] border border-[#1F2426] rounded-xl p-4 space-y-1">
          <div className="flex items-center justify-between text-xs text-amber-400/90 font-medium">
            <span>💸 Outstanding</span>
            <span className="text-[10px] text-gray-500 font-mono">{unpaidBookings.length} unpaid</span>
          </div>
          <p className="text-2xl font-bold font-mono text-amber-400/90">
            <PrivacyAmount amount={outstandingTotal} prefix="R" isPrivacyMode={isPrivacyMode} />
          </p>
        </div>

        {/* Collected */}
        <div className="bg-[#151819] border border-[#1F2426] rounded-xl p-4 space-y-1">
          <div className="flex items-center justify-between text-xs text-[#14B8A6] font-medium">
            <span>💰 Collected</span>
            <span className="text-[10px] text-gray-500 font-mono">{paidBookings.length} paid</span>
          </div>
          <p className="text-2xl font-bold font-mono text-[#14B8A6]">
            <PrivacyAmount amount={collectedTotal} prefix="R" isPrivacyMode={isPrivacyMode} />
          </p>
        </div>

        {/* Active Billed / Total */}
        <div className="bg-[#151819] border border-[#1F2426] rounded-xl p-4 space-y-1">
          <div className="flex items-center justify-between text-xs text-gray-400 font-medium">
            <span>🧾 Active Invoiced</span>
            <span className="text-[10px] text-gray-500 font-mono">
              {unpaidBookings.length + paidBookings.length} active
              {cancelledBookings.length > 0 && ` (${cancelledBookings.length} void)`}
            </span>
          </div>
          <p className="text-2xl font-bold font-mono text-white">
            <PrivacyAmount amount={activeRevenueTotal} prefix="R" isPrivacyMode={isPrivacyMode} />
          </p>
        </div>
      </div>

      {/* 2.5 SEARCH & STATUS FILTER CONTROLS */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 bg-[#151819] border border-[#1F2426] p-3 rounded-xl">
        {/* Search input */}
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search by client, invoice # or stream..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-lg pl-9 pr-8 py-2 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-[#14B8A6]"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white p-0.5"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Status Filter Segmented Control */}
        <div className="flex items-center gap-1 bg-[#0C0E0F] p-1 border border-[#2A2F31] rounded-lg overflow-x-auto shrink-0">
          <button
            type="button"
            onClick={() => setStatusFilter('all')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
              statusFilter === 'all'
                ? 'bg-[#14B8A6] text-black font-semibold shadow'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            All ({bookings.length})
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter('unpaid')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
              statusFilter === 'unpaid'
                ? 'bg-amber-400 text-black font-semibold shadow'
                : 'text-gray-400 hover:text-amber-300'
            }`}
          >
            Unpaid ({unpaidBookings.length})
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter('paid')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
              statusFilter === 'paid'
                ? 'bg-[#14B8A6] text-black font-semibold shadow'
                : 'text-gray-400 hover:text-[#14B8A6]'
            }`}
          >
            Paid ({paidBookings.length})
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter('cancelled')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
              statusFilter === 'cancelled'
                ? 'bg-rose-500 text-white font-semibold shadow'
                : 'text-gray-400 hover:text-rose-300'
            }`}
          >
            Cancelled ({cancelledBookings.length})
          </button>
        </div>
      </div>

      {/* 3. INVOICE LIST */}
      <div className="space-y-6">
        {/* Outstanding Section (Unpaid FIRST) */}
        {(statusFilter === 'all' || statusFilter === 'unpaid') && (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wider text-amber-400/90 px-1">
              <span>Outstanding</span>
              <span className="text-[10px] font-mono font-normal text-gray-500">{filteredUnpaid.length} pending</span>
            </div>

            {filteredUnpaid.length === 0 ? (
              <div className="p-6 bg-[#151819] border border-[#1F2426] rounded-xl text-center text-xs text-gray-400 space-y-1">
                <p className="font-semibold text-white">
                  {search ? 'No unpaid invoices match search.' : 'All clear! No outstanding invoices.'}
                </p>
                <p className="text-gray-500 text-[11px]">New bookings will automatically generate unpaid invoices here.</p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {filteredUnpaid.map((b) => (
                  <div
                    key={b.id}
                    onClick={() => setSelectedInvoice(b)}
                    className="flex justify-between items-center bg-[#151819] border border-[#1F2426] hover:border-amber-500/30 rounded-xl p-4 hover:translate-y-[-2px] transition-all duration-200 cursor-pointer group"
                  >
                    <div className="space-y-1 min-w-0 pr-2">
                      <div className="text-white font-medium text-sm flex items-center gap-2 flex-wrap">
                        <span className="truncate">{b.client_name || b.type}</span>
                        <span className="text-[10px] text-gray-500 font-mono shrink-0">
                          {b.invoice_number || `INV-${b.id}`}
                        </span>
                      </div>
                      <div className="text-gray-500 text-xs flex items-center gap-2 flex-wrap">
                        <span>{new Date(b.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        <span>•</span>
                        <span className="capitalize text-[#14B8A6]">{b.type}</span>
                        {b.due_date && (
                          <>
                            <span>•</span>
                            <DueDateBadge dueDateStr={b.due_date} isPaid={false} />
                          </>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-white font-semibold font-mono text-sm sm:text-base mr-1">
                        <PrivacyAmount amount={b.price} prefix="R" isPrivacyMode={isPrivacyMode} />
                      </span>
                      <button
                        type="button"
                        onClick={(e) => handleDownloadPDF(b, e)}
                        className="bg-[#2A2F31] text-gray-300 hover:text-white hover:bg-[#343A3D] transition-all p-1.5 rounded-md text-xs cursor-pointer"
                        title="Download Invoice PDF"
                      >
                        <FileText size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onUpdateBookingStatus(b.id, 'paid');
                        }}
                        className="bg-[#2A2F31] text-gray-300 hover:bg-[#14B8A6] hover:text-black transition-all px-2.5 py-1 rounded-md text-xs font-semibold cursor-pointer shrink-0"
                        title="Click to Mark as Paid"
                      >
                        Mark Paid
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onUpdateBookingStatus(b.id, 'cancelled');
                        }}
                        className="bg-rose-950/40 text-rose-400 hover:bg-rose-600 hover:text-white border border-rose-800/40 transition-all px-2.5 py-1 rounded-md text-xs font-semibold cursor-pointer shrink-0 flex items-center gap-1"
                        title="Cancel this invoice (void without deleting)"
                      >
                        <Ban size={12} /> Cancel
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Paid Section (Paid AFTER) */}
        {(statusFilter === 'all' || statusFilter === 'paid') && (
          <div className="space-y-3 pt-2">
            <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wider text-[#14B8A6] px-1">
              <span>Paid</span>
              <span className="text-[10px] font-mono font-normal text-gray-500">{filteredPaid.length} completed</span>
            </div>

            {filteredPaid.length === 0 ? (
              <div className="p-6 bg-[#151819] border border-[#1F2426] rounded-xl text-center text-xs text-gray-500">
                {search ? 'No paid invoices match search.' : 'No collected payments recorded yet.'}
              </div>
            ) : (
              <div className="space-y-2.5">
                {filteredPaid.map((b) => (
                  <div
                    key={b.id}
                    onClick={() => setSelectedInvoice(b)}
                    className="flex justify-between items-center bg-[#151819] border border-[#1F2426] hover:border-[#14B8A6]/30 rounded-xl p-4 hover:translate-y-[-2px] transition-all duration-200 cursor-pointer group"
                  >
                    <div className="space-y-1 min-w-0 pr-2">
                      <div className="text-white font-medium text-sm flex items-center gap-2 flex-wrap">
                        <span className="truncate">{b.client_name || b.type}</span>
                        <span className="text-[10px] text-gray-500 font-mono shrink-0">
                          {b.invoice_number || `INV-${b.id}`}
                        </span>
                      </div>
                      <div className="text-gray-500 text-xs flex items-center gap-2 flex-wrap">
                        <span>{new Date(b.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        <span>•</span>
                        <span className="capitalize text-[#14B8A6]">{b.type}</span>
                        {b.due_date && (
                          <>
                            <span>•</span>
                            <DueDateBadge dueDateStr={b.due_date} isPaid={true} />
                          </>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-white font-semibold font-mono text-sm sm:text-base mr-1">
                        <PrivacyAmount amount={b.price} prefix="R" isPrivacyMode={isPrivacyMode} />
                      </span>
                      <button
                        type="button"
                        onClick={(e) => handleDownloadPDF(b, e)}
                        className="bg-[#2A2F31] text-gray-300 hover:text-white hover:bg-[#343A3D] transition-all p-1.5 rounded-md text-xs cursor-pointer"
                        title="Download Invoice PDF"
                      >
                        <FileText size={14} />
                      </button>
                      <span className="bg-[#14B8A6] text-black font-bold px-2.5 py-1 rounded-md text-xs shrink-0">
                        Paid ✓
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Cancelled Section (Void / Cancelled records) */}
        {(statusFilter === 'all' || statusFilter === 'cancelled') && filteredCancelled.length > 0 && (
          <div className="space-y-3 pt-2">
            <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wider text-rose-400 px-1">
              <span className="flex items-center gap-1.5">
                <Ban size={13} /> Cancelled &amp; Void Invoices
              </span>
              <span className="text-[10px] font-mono font-normal text-gray-500">{filteredCancelled.length} void</span>
            </div>

            <div className="space-y-2.5">
              {filteredCancelled.map((b) => (
                <div
                  key={b.id}
                  onClick={() => setSelectedInvoice(b)}
                  className="flex justify-between items-center bg-[#151819]/60 border border-rose-900/30 hover:border-rose-700/50 rounded-xl p-4 hover:translate-y-[-2px] transition-all duration-200 cursor-pointer group opacity-85 hover:opacity-100"
                >
                  <div className="space-y-1 min-w-0 pr-2">
                    <div className="text-gray-300 font-medium text-sm flex items-center gap-2 flex-wrap">
                      <span className="truncate line-through text-gray-400">{b.client_name || b.type}</span>
                      <span className="text-[10px] text-gray-500 font-mono shrink-0">
                        {b.invoice_number || `INV-${b.id}`}
                      </span>
                    </div>
                    <div className="text-gray-500 text-xs flex items-center gap-2 flex-wrap">
                      <span>{new Date(b.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      <span>•</span>
                      <span className="capitalize text-gray-400">{b.type}</span>
                      <span className="text-rose-400 text-[10px] font-medium">• Excluded from revenue &amp; tax</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-gray-500 font-semibold font-mono text-sm sm:text-base mr-1 line-through">
                      <PrivacyAmount amount={b.price} prefix="R" isPrivacyMode={isPrivacyMode} />
                    </span>
                    <button
                      type="button"
                      onClick={(e) => handleDownloadPDF(b, e)}
                      className="bg-[#2A2F31] text-gray-300 hover:text-white hover:bg-[#343A3D] transition-all p-1.5 rounded-md text-xs cursor-pointer"
                      title="Download Cancelled Invoice PDF"
                    >
                      <FileText size={14} />
                    </button>
                    <span className="bg-rose-500/20 text-rose-400 border border-rose-500/30 font-bold px-2.5 py-1 rounded-md text-xs shrink-0">
                      Cancelled
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onUpdateBookingStatus(b.id, 'unpaid');
                      }}
                      className="bg-[#2A2F31] text-gray-300 hover:bg-amber-400 hover:text-black transition-all px-2.5 py-1 rounded-md text-xs font-semibold cursor-pointer shrink-0 flex items-center gap-1"
                      title="Restore invoice as Unpaid"
                    >
                      <RotateCcw size={12} /> Restore
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 4. EXPAND / MODAL FOR ROW DETAILS */}
      {selectedInvoice && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-[#151819] border border-[#2A2F31] rounded-2xl p-6 max-w-lg w-full space-y-5 relative shadow-2xl max-h-[90vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-800">
            <button
              onClick={() => setSelectedInvoice(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
            >
              <X size={18} />
            </button>

            <div className="space-y-1">
              <span className="text-[10px] text-[#14B8A6] font-mono uppercase font-bold tracking-wider">INVOICE PREVIEW &amp; DETAILS</span>
              <h3 className="text-xl font-bold text-white">{selectedInvoice.client_name}</h3>
              <p className="text-xs text-gray-400 font-mono">{selectedInvoice.invoice_number || `INV-${selectedInvoice.id}`}</p>
            </div>

            {selectedInvoice.status === 'cancelled' && (
              <div className="bg-rose-950/30 border border-rose-800/40 rounded-xl p-3 text-xs text-rose-300 space-y-1">
                <div className="flex items-center gap-1.5 font-bold text-rose-400">
                  <Ban size={14} /> CANCELLED / VOID INVOICE
                </div>
                <p className="text-[11px] text-rose-300/80 leading-relaxed">
                  This invoice is marked as Cancelled. It does not count towards total revenue, gross receipts, VAT output liabilities, or SARS annual tax returns.
                </p>
              </div>
            )}

            {/* FROM and TO breakdown */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl p-3.5 text-xs">
              <div className="space-y-1 border-b sm:border-b-0 sm:border-r border-[#1F2426] pb-3 sm:pb-0 sm:pr-3">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold text-[#14B8A6] uppercase tracking-wider">FROM (ISSUER)</span>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedInvoice(null);
                      setIsSettingsOpen(true);
                    }}
                    className="text-[10px] text-gray-400 hover:text-[#14B8A6] underline cursor-pointer"
                  >
                    Edit
                  </button>
                </div>
                <p className="font-semibold text-white truncate">{businessSettings.companyName || businessSettings.senderName || 'Your Business Name'}</p>
                {businessSettings.companyName && businessSettings.senderName && (
                  <p className="text-gray-400 text-[11px]">Attn: {businessSettings.senderName}</p>
                )}
                <p className="text-gray-400 text-[11px] truncate">{businessSettings.email}</p>
                {businessSettings.phone && <p className="text-gray-500 text-[10px]">{businessSettings.phone}</p>}
                {businessSettings.address && <p className="text-gray-500 text-[10px] truncate">{businessSettings.address}</p>}
              </div>

              <div className="space-y-1">
                <span className="text-[10px] font-bold text-[#14B8A6] uppercase tracking-wider block">TO (BILLED TO)</span>
                <p className="font-semibold text-white truncate">{selectedInvoice.client_name || 'Valued Client'}</p>
                {selectedInvoice.client_company && <p className="text-gray-400 text-[11px]">{selectedInvoice.client_company}</p>}
                {selectedInvoice.client_email && <p className="text-gray-400 text-[11px] truncate">{selectedInvoice.client_email}</p>}
                {selectedInvoice.client_phone && <p className="text-gray-500 text-[10px]">{selectedInvoice.client_phone}</p>}
                <p className="text-gray-500 text-[10px]">Client Ref: {selectedInvoice.id}</p>
              </div>
            </div>

            <div className="bg-[#0C0E0F] border border-[#2A2F31] rounded-xl p-4 space-y-3">
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-400">Stream / Category</span>
                <span className="text-white capitalize font-semibold">{selectedInvoice.type}</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-400">Invoice Date</span>
                <span className="text-gray-300">{new Date(selectedInvoice.date).toLocaleDateString()}</span>
              </div>
              {selectedInvoice.due_date && (
                <div className="flex justify-between items-center text-xs">
                  <span className="text-gray-400">Due Date</span>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-300">{new Date(selectedInvoice.due_date).toLocaleDateString()}</span>
                    <DueDateBadge dueDateStr={selectedInvoice.due_date} isPaid={selectedInvoice.status === 'paid'} />
                  </div>
                </div>
              )}
              <div className="space-y-1.5 pt-2 border-t border-[#1F2426]">
                <div className="flex justify-between items-center text-xs text-gray-400">
                  <span>Subtotal</span>
                  <span className={`font-mono ${selectedInvoice.status === 'cancelled' ? 'line-through text-gray-500' : 'text-gray-300'}`}>
                    <PrivacyAmount amount={selectedInvoice.price} prefix="R" isPrivacyMode={isPrivacyMode} />
                  </span>
                </div>
                <div className="flex justify-between items-center text-xs text-gray-400">
                  <span>VAT (15%)</span>
                  <span className={`font-mono ${selectedInvoice.status === 'cancelled' ? 'line-through text-gray-500' : 'text-gray-300'}`}>
                    <PrivacyAmount amount={selectedInvoice.price * 0.15} prefix="R" isPrivacyMode={isPrivacyMode} />
                  </span>
                </div>
                <div className="flex justify-between items-center text-xs pt-1 border-t border-[#2A2F31]">
                  <span className="text-gray-300 font-semibold">Total (incl. VAT)</span>
                  <span className={`text-lg font-bold font-mono ${selectedInvoice.status === 'cancelled' ? 'line-through text-rose-400' : 'text-[#14B8A6]'}`}>
                    <PrivacyAmount amount={selectedInvoice.price * 1.15} prefix="R" isPrivacyMode={isPrivacyMode} />
                  </span>
                </div>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-400 font-medium">Current Status</span>
                {selectedInvoice.status === 'paid' ? (
                  <span className="bg-[#14B8A6] text-black font-bold px-2 py-0.5 rounded text-[11px]">Paid ✓</span>
                ) : selectedInvoice.status === 'cancelled' ? (
                  <span className="bg-rose-500/20 text-rose-400 border border-rose-500/30 font-bold px-2 py-0.5 rounded text-[11px]">Cancelled / Void</span>
                ) : (
                  <span className="bg-[#2A2F31] text-gray-400 font-bold px-2 py-0.5 rounded text-[11px]">Unpaid</span>
                )}
              </div>

              {businessSettings.paymentLink && selectedInvoice.status !== 'cancelled' && (
                <div className="pt-2 border-t border-[#1F2426] space-y-1">
                  <span className="text-[10px] text-[#14B8A6] font-bold uppercase tracking-wider block">INVOICE ONLINE PAYMENT LINK</span>
                  <div className="flex items-center justify-between bg-[#151819] border border-[#2A2F31] rounded-lg p-2 text-xs">
                    <span className="text-gray-300 font-mono truncate mr-2">{businessSettings.paymentLink}</span>
                    <a
                      href={businessSettings.paymentLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#14B8A6] hover:underline text-[11px] font-bold shrink-0 flex items-center gap-1"
                    >
                      <ExternalLink size={12} /> Open
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* ACTION BUTTONS */}
            <div className="space-y-2.5">
              <button
                type="button"
                onClick={() => handleDownloadPDF(selectedInvoice)}
                disabled={isGeneratingPDF}
                className="w-full bg-[#1F2426] hover:bg-[#2A2F31] border border-[#2A2F31] text-gray-200 hover:text-white font-semibold rounded-xl px-4 py-3 text-xs flex items-center justify-center gap-2 transition-all cursor-pointer"
              >
                {isGeneratingPDF && pdfBooking?.id === selectedInvoice.id ? (
                  <>
                    <Loader2 size={15} className="text-[#14B8A6] animate-spin" /> Generating PDF...
                  </>
                ) : (
                  <>
                    <FileText size={15} className="text-[#14B8A6]" /> Download PDF Invoice
                  </>
                )}
              </button>

              {selectedInvoice.status === 'paid' ? (
                <div className="space-y-2 pt-1">
                  <button
                    disabled
                    className="w-full bg-[#14B8A6]/20 text-[#14B8A6] border border-[#14B8A6]/30 font-bold rounded-xl px-4 py-2.5 text-xs flex items-center justify-center gap-2 cursor-default"
                  >
                    Paid ✓
                  </button>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        onUpdateBookingStatus(selectedInvoice.id, 'unpaid');
                        setSelectedInvoice({ ...selectedInvoice, status: 'unpaid' });
                      }}
                      className="w-full bg-[#1F2426] hover:bg-[#2A2F31] text-gray-300 font-semibold rounded-xl py-2 text-xs transition-colors text-center cursor-pointer"
                    >
                      Mark as Unpaid
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        onUpdateBookingStatus(selectedInvoice.id, 'cancelled');
                        setSelectedInvoice({ ...selectedInvoice, status: 'cancelled' });
                      }}
                      className="w-full bg-rose-950/40 hover:bg-rose-900/60 border border-rose-800/40 text-rose-300 font-semibold rounded-xl py-2 text-xs transition-colors text-center cursor-pointer"
                    >
                      Cancel / Void
                    </button>
                  </div>
                </div>
              ) : selectedInvoice.status === 'cancelled' ? (
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      onUpdateBookingStatus(selectedInvoice.id, 'unpaid');
                      setSelectedInvoice({ ...selectedInvoice, status: 'unpaid' });
                    }}
                    className="w-full bg-amber-400 hover:bg-amber-300 text-black font-bold rounded-xl px-4 py-3 text-xs transition-all shadow-md active:scale-95 cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <RotateCcw size={13} /> Restore as Unpaid
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      onUpdateBookingStatus(selectedInvoice.id, 'paid');
                      setSelectedInvoice({ ...selectedInvoice, status: 'paid' });
                    }}
                    className="w-full bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-bold rounded-xl px-4 py-3 text-xs transition-all shadow-md active:scale-95 cursor-pointer"
                  >
                    Mark as Paid
                  </button>
                </div>
              ) : (
                <div className="space-y-2 pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      onUpdateBookingStatus(selectedInvoice.id, 'paid');
                      setSelectedInvoice({ ...selectedInvoice, status: 'paid' });
                    }}
                    className="w-full bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-bold rounded-xl px-4 py-3 text-xs transition-all shadow-lg active:scale-95 cursor-pointer"
                  >
                    Mark as Paid
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      onUpdateBookingStatus(selectedInvoice.id, 'cancelled');
                      setSelectedInvoice({ ...selectedInvoice, status: 'cancelled' });
                    }}
                    className="w-full bg-rose-950/30 hover:bg-rose-900/50 border border-rose-800/40 text-rose-400 hover:text-rose-300 font-semibold rounded-xl py-2.5 text-xs transition-all text-center cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <Ban size={13} /> Cancel Invoice (Mark Void)
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* BUSINESS SETTINGS MODAL */}
      <BusinessSettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        onSaved={(updated) => setBusinessSettings(updated)}
        isPrivacyMode={isPrivacyMode}
        onTogglePrivacyMode={onTogglePrivacyMode}
      />

      {/* HIDDEN INVOICE PDF TEMPLATE FOR CANVAS CAPTURE */}
      {pdfBooking && (
        <div className="fixed -left-[9999px] -top-[9999px] opacity-0 pointer-events-none" aria-hidden="true">
          <InvoicePDFTemplate booking={pdfBooking} businessSettings={businessSettings} id="invoice-pdf-hidden-template" />
        </div>
      )}
    </div>
  );
});
